/**
 * PaperComposeModal — 시험지 제작(좌 검색 · 중 담은 문제 · 우 지면 미리보기).
 *
 * ══ 폭이 좁아지면 **지면부터 접는다**(오너 지시 2026-08-29) ═════════════════
 * xl(1280px) 이상은 세 열이 나란히 선다. 그 아래에서는 지면이 **오른쪽 서랍**으로 물러나고,
 * 검색과 담은 문제 **두 열은 어떤 폭에서도 남는다**. 서랍은 오른쪽 가장자리 손잡이로 열고
 * 닫으며 오른쪽에서 왼쪽으로 미끄러진다.
 *
 * 접는 것을 지면으로 고른 이유는 그것이 **결과**이고 앞의 둘은 **손**이기 때문이다 —
 * 문제를 고르고(좌) 자리를 잡는(중) 일은 두 열이 함께 있어야 성립한다.
 *
 * 경계를 lg(1024)가 아니라 xl(1280)에 둔 근거는 **검색 카드의 본문 글자 크기**다.
 * 카드 본문은 지면 한 단(423px)을 통째로 축소해 그리므로 배율 = 칸 너비 / 423 이고
 * 화면 글자 = 18px × 배율이다(ProblemBodyPreview).
 * 실측(2026-08-29 · 빌드된 CSS 를 그대로 물린 격리 하네스 · 열 수만 바꿔 같은 자로 잼):
 *
 *   화면 1440 → 3열 검색 448.0 → 칸 365 → 배율 0.86 → 글자 15.5px
 *   화면 1280 → 3열 검색 401.4 → 칸 318 → 배율 0.75 → 글자 13.5px
 *   화면 1024 → 3열 검색 321.1 → 칸 238 → 배율 0.56 → 글자 **10.1px** ← 하한(12px) 미달
 *   화면 1024 → 2열 검색 484.3 → 칸 401 → 배율 0.95 → 글자 **17.1px**
 *
 * 3열이 12px 을 지키는 화면 하한은 **1165px**(1162 에서 11.9px 로 내려간다). 기성 단계 중
 * 그 위 첫 값이 xl 이라 여기에 뒀다 — lg 로 두면 1024~1279 구간이 10.1~13.5px 로 하한을 깬다.
 * 2열의 같은 하한은 **780px**(776 에서 11.9px). 그 아래는 2열이어도 본문이 작아지는데,
 * 그럼에도 더 접지 않는 것은 「최소 2열」이 지시이기 때문이다.
 *
 * 덤으로 **지면이 커진다.** 세 번째 열일 때 지면 칸은 화면 1440 에서 492px 뿐인데, 서랍은 폭의
 * 86% 를 가져가 화면 1279 에서 1038px · 1024 에서 823px 이 된다.
 * (괄호 안에 적혀 있던 배율 0.39/0.83/0.66 은 **v2 지면(328mm=1240px) 때의 값**이다. 지금은
 *  용지 실치수로 나누므로 같은 칸에서 A4 는 0.61/1.30/1.03, B4 는 0.50/1.06/0.84 로 커진다 —
 *  계산값이다. 서랍에서는 100% 를 넘어 실물 크기가 된다.)
 *
 * ── 강의 칸을 펴면 지면이 서랍으로(오너 결정 2026-09-14) ──
 * 강의 나무는 검색 열 왼쪽의 붙박이 칸(320px)이다. 창을 열면 펼쳐져 있고 손잡이로 접는다(전에는 검색 열을 덮는 덮개였다).
 * 네 열이 설 자리는 없다 — 화면 1440 에서 세 열(검색 438 · 담은 문제 410 · 지면 493)이 창을 다 쓴다. 그래서 펼친 동안은
 * 폭과 상관없이 지면이 xl 미만처럼 오른쪽 서랍으로 물러나고, 검색·담은 문제 두 열이 남는 폭을 반씩 갖는다(1440 에서 각 약 490).
 * 접으면 위의 규칙으로 돌아간다. 그래서 열/서랍 판정은 CSS 의 xl: 이 아니라 paperAsColumn 하나로 한다.
 *
 * ── 창이 화면 폭을 따라 는다(오너 2026-09-15 「가로폭을 유동적으로 · 넓으면 지면이 숨지 않게」) ──
 * 창의 1400px 상한을 걷었다(98vw). 그래서 넓은 화면에서는 강의 칸을 편 채로도 네 칸이 다 선다.
 * 판정은 화면 폭이 아니라 **열 상자의 실제 폭**이다(paperColumn.composePaperAsColumn): 강의 칸(320)을 뺀 폭이 위 xl 경계에서의
 * 폭(1222) 이상이면 열이다. 강의 칸을 접으면 종전과 똑같이 화면 1280 에서, 펴면 화면 약 1607 에서 바뀐다(1440·1512 는 여전히 서랍).
 * 세 열의 몫(검색 32% · 담은 문제 30%)도 **강의 칸을 뺀 폭**에서 잰다 — 경계에서 검색 열이 종전 xl 경계와 같은 391px 이 된다(실측 2026-09-15).
 * ══════════════════════════════════════════════════════════════════════════
 *
 * 기존 시험지 제작의 3열 감각을 시험지(/exam-papers) 화면에 맞게 옮긴 것이다.
 * 좌측에는 개념·라벨 검색(problem-search)을 인라인으로 편다.
 *
 * 우측은 실제 지면이다 — **현행 인쇄 v4**(src/print/PrintSheetV4)를 이 열 한 칸에 그대로 그린다
 * (2026-08-29. 그전에는 v2 인라인 렌더러였다 — 328mm 가짜 캔버스라 A4 도 B4 도 아닌 판이었다).
 * 규격(printSpec)·측정(measureV4)·면 배열(pagePatterns)·조판·빠른정답(useAnswers)까지 인쇄와
 * **같은 부품**이고, 용지(A4/B4)·B4 머리글·면 배열 프리셋은 전체화면 [미리보기]에서 고른 값을
 * 읽는다(printPrefs). 그래서 「화면이 보여준 용지 = 실제로 인쇄되는 용지」가 구조적으로 보장된다.
 * 담기·빼기·순서 이동은 실측(mm)을 거쳐 지면에 반영된다.
 * 전체화면 [미리보기]는 그대로 둔다 — 인쇄·PDF 는 여전히 그쪽 몫이고 우측은 축소판이다.
 *
 * 목록과 지면은 같은 문제를 선택한다. 교체·순서 변경은 선택 카드의 명시적인 버튼으로 실행한다.
 *
 * 계약: BE-20260825-exam-papers-screens.md §2·§3(POST /papers · PATCH /papers/{id}) ·
 *       BE-20260825-fe-handover.md §D(problem-search needs_analysis).
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, Check, Minus, Plus, ArrowUp, ArrowDown, Trash2, Save, Replace, GripVertical, BookMarked, Loader2 } from 'lucide-react';
import { Modal, ConfirmModal, Button, MathceptDetailModal, DesktopPagination } from '../common';
// 페이지 이동 부품의 생김새는 이 파일에 있다 — 쓰는 쪽이 함께 들이는 것이 이 저장소 관례다
// (ProblemSearchPanel·AnalysisJobsModal 과 같다). 청크가 갈려도 스타일이 따라온다.
import '../../styles/desktop-expanded.css';
import { useAuth } from '../../context/AuthContext';
import { fetchWithTokenRefresh } from '../../utils/apiUtils';
import { createPaper, updatePaper, fetchPaper, isDeletedItem, describeApiError, AUTH_REDIRECT } from './examPaperApi';
import { getOriginDisplay, difficultyBadgeClass, conceptAllExpr, CONCEPT_ALL_UNAVAILABLE_MSG } from '../ProblemHubV2/problemApi';
// 문제 번호 — 두 메뉴가 함께 쓰는 정본 부품(문제 v2 목록 표가 기준 얼굴이다).
// 이 창은 저장하지 않은 편집을 들고 있으므로 자기 확인 함수를 ProblemNavGuard 로 덮어씌운다.
import ProblemIdLink, { ProblemNavGuard } from '../ProblemHubV2/ProblemIdLink';
// 담은 문항의 조판 본문은 **v2 한 길**로만 받는다 — 검색과 같은 엔드포인트에 problem_ids 를
// 실어 묶음으로 묻는 이음매다(계약 §9). 우측 지면의 v1(/api/problems/print/page)을 여기 끌어오지
// 않는다(오너 방침 2026-08-26 — 이 화면은 v2 전용이다).
// item 을 통째로 받는다 — 본문(body_lexical)과 **개념·난이도가 같은 응답에 실려 온다**.
// 본문만 뽑던 fetchProblemBodiesV2 를 쓰면 배지를 그리려고 같은 자리를 한 번 더 물어야 한다.
import { fetchProblemItemsV2 } from './problemBodyV2';
// Lexical editorState → 조판 HTML. 같은 변환기가 이 폴더에 두 벌 있던 것을 한 벌로 뽑았다
// (2026-08-28) — 문항 편집 목록이 세 번째로 같은 것을 필요로 하면서다.
import { toBodyHtmlMap } from './lexicalHtml';
// 본문 미리보기 마크업 한 벌 — 좌측 검색 카드와 가운데 담은 문항이 함께 쓴다
import ProblemBodyPreview from './ProblemBodyPreview';
import OverflowRow from './OverflowRow';
// 개념 사전(v2) — 검색 조건의 개념 칩이 이름·순번·유형을 얻는 곳. 모듈 캐시(5분)라 이 화면이
// 처음 부르는 값이어도 왕복은 세션당 한 번에 그친다. v1 은 끌어오지 않는다(오너 방침 2026-08-26).
import { ConceptBadge, fetchConceptDictV2, fetchConceptDetailV2 } from './conceptBadge';
// 라벨 배지 — 문제 허브의 표·카드·상세와 **같은 부품**(2026-09-02). 줄 간격만 이 자리 값을 준다.
import { LabelBadges } from './labelBadge';
// 응답의 개념 자리 → [{id,name}]. 세 형태(객체배열·문자열배열·맵)를 한 곳에서 흡수한다
import { conceptPairsOf } from './paperStats';
import ConceptFilterInput from './ConceptFilterInput';
import ConceptMatchToggle, { ConceptAllHint } from './ConceptMatchToggle';
// 강의(검색 프리셋 나무) — 왼쪽 손잡이로 여는 덮개와 본문 검색(docs/learning-composition-v2/BE-20260911-reply-syllabus-preset.md §5)
import SyllabusPickOverlay from '../SyllabusV2/SyllabusPickOverlay';
import { searchByExpr, describeSyllabusError, isPredeployError } from '../SyllabusV2/syllabusApi';
import { inspectExpr } from '../SyllabusV2/syllabusQuery';
import { readLabelNames, rememberLabelNames } from '../SyllabusV2/LabelPicker';
import { warningLines, timeText } from '../SyllabusV2/SearchResults';
import SyllabusComposeConditions from './SyllabusComposeConditions';
import ProblemAnswerDisclosure from './ProblemAnswerDisclosure';
// 우측 지면은 공용 부품이다(2026-09-14 조립 창에서 떼어 냄 — 학생 화면 「시험지 불러오기」 창과 같은 지면).
// 계산(현행 인쇄 v4 규격·측정·배치·정답·배율·전체화면 미리보기)은 usePaperSheet 가, 얼굴(머리·스크롤 칸·손잡이)은
// PaperSheetView 가 갖는다. 이 창에 남은 것은 열/서랍 판정(paperAsColumn)·교체(지면 클릭)·지면으로 데려가기다.
import usePaperSheet from './usePaperSheet';
import { createPrintDraftContext, promotePrintDraft } from '../../print/printDrafts';
import PaperSheetView, { PaperSheetHandle, PaperSheetFullPreview } from './PaperSheetView';
// 지면 컨테이너 안에서만 이동하는 기존 좌표 계산과 스크롤 함수를 재사용한다.
import { centerTarget, scrollBoxTo } from './focusRing';
import { movePaperRow, movePaperRowAtBoundary, swapPaperRows, uniqueRowIndex } from './paperOrder';
import usePaperListDrag from './usePaperListDrag';
import PaperRowActions from './PaperRowActions';
// 지면을 열로 세울지 서랍에 넣을지 — 창 안 열 상자의 실제 폭으로 잰다(2026-09-15, 테스트 생성 창과 같은 자)
import {
  useBoxWidth, composePaperAsColumn, composeColumnWidth, COMPOSE_SEARCH_SHARE, COMPOSE_ITEMS_SHARE,
} from './paperColumn';
import HubAssignmentConfirm from '../StudentsHub/HubAssignmentConfirm';

// 좌측 검색 결과가 보여 주는 preview 는 평문 140자라 수식이 LaTeX 원문 그대로 보인다.
// 한 문제의 실제 지면을 보려면 이 창을 쓴다 — **v2 전용** 미리보기(오너 지시 2026-08-26).
// 구형 ProblemPreview 는 손대지 않는다: v1 셋(/api/problems/print · /api/mathcepts/temp ·
// …/by-prob-ids)에 묶여 있고 다른 화면 여럿이 그대로 쓴다. 이쪽은 문제당 v2 상세 한 번이다.
// lexical·katex 를 끌고 오므로 누르기 전까지 받지 않는다(우측 지면과 같은 관례).

const PER_PAGE = 20;
/**
 * 결과 머리의 [전체 선택]이 한 번에 고르는 수의 상한 — 오너 결정(2026-10-09). 결과가 이보다 많으면
 * **맨 위(검색 순서 = 등록 최신순)부터 이만큼만** 고른다. 체크칸 글 「전체 선택 (최대 100문제)」이 이 상한을 늘 말한다.
 * 값이 problem-search 의 per_page 상한(5~100, BE-20260830-reply §4-4)과 같아 **한 번의 왕복**으로 끝난다.
 * 100 위로 올리면 서버가 조용히 100 으로 잘라 주어 「다 골랐다」가 거짓이 된다 — 올리려면 여러 쪽을 이어 받게 고친다.
 */
const PICK_TOP_LIMIT = 100;

// 기존 import 경로를 유지한다. 드래그 세션과 MIME은 전용 훅이 관리한다.
export { PROBLEM_DND_MIME } from './usePaperListDrag';
const EMPTY_FORM = {
  conceptIds: [], labels: '', difficulty: '', problemIds: '',
  author: '', hasAnswer: '', hasSolution: '', needsAnalysis: false,
  // 개념 칸 안쪽 OR/AND — 'any'(하나라도) | 'all'(모두). 강의 조건을 고른 동안에는 폼째 감추고 싣지 않는다(2026-09-14 — D3 잠금을 걷었다)
  conceptMatch: 'any',
};

// needs_analysis 를 켜면 함께 오는 분석 이력 상태(BE-20260825-reply-batch §3-1).
// 110 은 OCR 스테이징 값이라 은행 검색에는 오지 않는다(같은 문서에서 정정됨).
const ANALYSIS_STATUS = { 100: '분석 대기', 150: '분석 중', 200: '분석 완료', 500: '분석 실패' };
const NEED_BADGES = [['need_concept', '개념'], ['need_difficulty', '난이도'], ['need_answer', '정답'], ['need_solution', '풀이']];

/**
 * 검색 폼 → 주소줄 파라미터(키·값 모두 문자열, 빈 칸은 뺀다).
 * 주소줄 검색과 강의 줄의 본문 검색(filters)이 **같은 키·뜻**을 쓴다(강의 회신 §5 S1) — 한 곳에서 만든다.
 */
function formToFilters(f) {
  const out = {};
  const put = (key, v) => {
    const s = String(v ?? '').trim();
    if (s !== '') out[key] = s;
  };
  // 개념은 **이름이 아니라 id** 로 건다(계약 §3 — `concept_ids` 는 "하나라도 포함").
  // 이름 부분검색(concept_q)은 맞는 개념이 200개를 넘으면 400 이라 넓은 말이 통째로 막히고,
  // 「미분」이 「미분계수」·「음함수의 미분법」까지 끌어온다. id 로 고르면 둘 다 없다.
  put('concept_ids', f.conceptIds.join(','));
  put('labels', f.labels.split(',').map((s) => s.trim()).filter(Boolean).join(','));
  put('difficulty', f.difficulty);
  put('problem_ids', f.problemIds.replace(/\s/g, ''));
  put('author', f.author);
  if (f.hasAnswer !== '') out.has_answer = f.hasAnswer === 'yes' ? 'true' : 'false';
  if (f.hasSolution !== '') out.has_solution = f.hasSolution === 'yes' ? 'true' : 'false';
  if (f.needsAnalysis) out.needs_analysis = 'true';
  return out;
}

// 요청의 의미를 비교한다 — 이름·행 ID는 문제 집합을 바꾸지 않는다.
const cloneQuery = (q) => q == null ? null : JSON.parse(JSON.stringify(q));
// 개념 방식도 싣는다 — 개념이 같아도 OR 와 AND 는 다른 문제 집합이다. 빠뜨리면 AND 로 바꿔도 「조건 변경됨」이
// 안 뜨고, 같은 조건의 OR 요청이 날아가는 중이면 AND 검색이 그 중복으로 여겨져 버려진다.

// 강의 식이 있으면 폼은 검색에 안 실린다(load) — 키에서도 뺀다. 안 빼면 감춰진 폼 값이 결과와 다르다고 「조건 변경됨」이 뜬다.
const composeSearchKey = (expr, form) => {
  if (expr != null) {
    const check = inspectExpr(expr, { mode: 'search' });
    return JSON.stringify({ expr: check.ok ? check.normalized : expr });
  }
  return JSON.stringify({ expr: null, filters: formToFilters(form), conceptMatch: form.conceptMatch ?? 'any' });
};

/**
 * 문제 검색 — problemApi.searchProblems 와 같은 엔드포인트지만 여기서 직접 부른다.
 * 그 함수는 아는 키만 골라 담아서 needs_analysis 를 넘기면 조용히 사라진다(빈손 결과가 아니라
 * "필터가 안 걸린 전체"가 와서 더 위험하다). 공용 API 파일은 이 작업의 소관 밖이라 손대지 않았다.
 */
async function searchForCompose(f, page, getAccessToken, { perPage = PER_PAGE, withBody = true } = {}) {
  // with_body=1 — 카드에 그릴 조판 본문(body_lexical)을 **검색 응답에 동봉**받는 스위치(계약 §9).
  // 오너 지시(2026-08-26) 「카드에 바로 미리보기」의 값이라 **응답이 커진다** — 한 페이지 20건이면
  // 문항 본문 20벌이 함께 온다. 그럼에도 이 길인 이유는 계약 §9 가 설계한 「별도 왕복 없음」이다:
  // 같은 엔드포인트를 problem_ids 로 한 번 더 부르면 페이지를 넘길 때마다 왕복이 1회씩 더 난다.
  // 본문이 없는 문항은 body_lexical:null 로 오고, 그 카드만 평문 preview 로 남는다.
  // [전체 선택]의 100개 받기는 카드를 그리지 않으므로 본문을 빼고 부른다(withBody:false).
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (withBody) params.set('with_body', '1');
  Object.entries(formToFilters(f)).forEach(([k, v]) => params.append(k, v));

  const res = await fetchWithTokenRefresh(`/api/v2/exam-papers/problem-search?${params}`, {}, getAccessToken);
  if (!res || res.status !== 'success') throw new Error(res?.message || '문제를 불러오지 못했습니다.');
  const d = res.data || {};
  const items = Array.isArray(d) ? d
    : Array.isArray(d.items) ? d.items
      : Array.isArray(d.list) ? d.list : [];
  return {
    items,
    page: d.page ?? page,
    totalItems: d.total_items ?? items.length,
    totalPages: d.total_pages ?? 1,
  };
}

/**
 * 개념 AND(모두 포함)의 식 — 강의 식이 없을 때만 폼에서 나온다. 「둘 이상일 때만」 규칙은
 * 문제(/problem-hub) 검색과 같은 conceptAllExpr 한 곳이다. 하나 이하면 null(주소줄 검색 그대로).
 */
const conceptAllExprOf = (expr, form) => (
  expr == null && form.conceptMatch === 'all' ? conceptAllExpr(form.conceptIds) : null
);

/**
 * 검색 한 번 — 세 갈래(강의 식 · 개념 AND · 주소줄)를 **여기 한 곳에서** 고른다.
 * 목록(load)과 결과 머리의 [전체 선택]이 같은 갈래를 타야 해서 떼어 냈다 — 두 벌로 두면 한쪽에서
 * 조건 하나가 빠져도 빌드는 통과하고, 화면에 보이던 것과 다른 문제가 골라진다.
 * expr 는 검사를 마친(normalized) 강의 식이거나 null 이다.
 */
function searchComposePage({ expr, form, page, perPage = PER_PAGE, withBody = true }, getAccessToken) {
  // 강의 식이 있으면 폼 조건은 싣지 않는다(filters: {}) — 강의를 고른 동안 폼은 감춰져 있다(2026-09-14 결과 좁히기 제거).
  // 폼 값은 그대로 남아 [강의 해제]하면 다시 쓰인다
  if (expr != null) return searchByExpr({ expr, filters: {}, page, perPage, withBody }, getAccessToken);
  // 개념 AND 는 본문 검색의 식으로만 나간다 — 주소줄의 concept_ids 는 「하나라도」뿐이다. 개념 칸은 식이
  // 맡으니 filters 에서 뺀다.
  const allExpr = conceptAllExprOf(null, form);
  if (allExpr != null) {
    return searchByExpr({ expr: allExpr, filters: formToFilters({ ...form, conceptIds: [] }), page, perPage, withBody }, getAccessToken);
  }
  return searchForCompose(form, page, getAccessToken, { perPage, withBody });
}

/**
 * 검색 응답에 실려 온 body_lexical → HTML 지도. 왕복하지 않는다 — 재료는 방금 받은 검색
 * 응답이다(계약 §9). 본문이 없는 문항(body_lexical:null)은 **키를 넣지 않는다** — 그 카드가
 * 평문 preview 로 폴백한다.
 */
function toSearchBodyMap(items) {
  const docs = {};
  (Array.isArray(items) ? items : []).forEach((it) => {
    const pid = it?.problem_id ?? it?.id;
    if (pid != null && it?.body_lexical != null) docs[String(pid)] = it.body_lexical;
  });
  return toBodyHtmlMap(docs);
}

/**
 * 상세의 문항 배열 — 계약 §2가 "현재 문항"이라고만 하고 키를 못박지 않았다.
 * 못 알아보면 null 을 돌려준다 — 빈 배열로 넘기면 화면이 "문항 없음"으로 보이고,
 * 첫 저장이 서버 문항을 통째로 지운 PATCH 가 된다(revision 도 올라 정상 편집처럼 보인다).
 * (PaperItemsEditor 와 같은 판단 — 두 화면이 같은 상세를 읽는다.)
 */
function pickItems(p) {
  if (p == null) return [];
  if (Array.isArray(p.items)) return p.items;
  if (Array.isArray(p.problems)) return p.problems;
  if (Array.isArray(p.snapshot?.items)) return p.snapshot.items;
  return null;
}

/** 라벨은 문자열로도 배열로도 온다 — 객체가 섞여도 "[object Object]"가 지면에 찍히지 않게 */
function labelText(v) {
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.filter((s) => typeof s === 'string').join(', ');
  return '';
}

/**
 * 서버 문항 → 편집 행. points 는 선택이라 없으면 빈 문자열(0과 구분).
 * isDeleted 는 계약 §1-3 의 items[].is_deleted 다 — 이 문항이 하나라도 있으면 배포·복사·되돌리기가
 * 409 로 막히므로 행이 그 사실을 말해야 한다.
 */
function toRows(items) {
  if (!Array.isArray(items)) return [];
  return items.map((it) => ({
    problem_id: it.problem_id ?? it.id,
    points: it.points == null ? '' : String(it.points),
    preview: it.preview ?? it.problem_preview ?? '',
    difficulty: it.difficulty ?? null,
    labels: labelText(it.labels),
    isDeleted: isDeletedItem(it),
    // 상세가 개념을 실어 주면 그대로 쓴다 — 계약이 못박지 않아 이름을 넓게 받는다.
    // 비면 본문 트랙이 받아 오는 item 에서 보충한다(rowItems).
    concepts: conceptPairsOf(it),
  }));
}

/**
 * 편집 행 → 요청 items. 배점을 비워 두면 키 자체를 보내지 않는다(0점이 아니다).
 *
 * **배점은 저장만 되고 채점에 쓰이지 않는다**(계약 §10, 오너 결정 2026-08-26). 그래서 이 화면은
 * 배점을 입력받지도 보여 주지도 않고, 서버가 준 값을 그대로 되돌려 보내 잃지 않게만 한다.
 * (계약 §10-3 의 「FE 미사용」은 **「백엔드 미사용(기록만)」의 오기**다 — BE-20260829-reply-fe-0828.md
 *  §1 3-2 가 정정했다. FE 는 지금처럼 보내고 읽는 것이 맞고, 백엔드가 채점·합계에 쓰지 않는다.)
 * 채점 연동이 생기기 전에 화면이 배점을 「점수」처럼 말하면 거짓이 된다 — 문구를 붙이지 마라.
 */
function toPayload(rows) {
  return rows.map((r) => {
    const o = { problem_id: Number(r.problem_id) };
    if (String(r.points).trim() !== '') o.points = Number(r.points);
    return o;
  });
}

/**
 * 보내기 직전 중복 검사(계약 §1-3) — 같은 problem_id 가 두 번 들어가면 POST/PATCH 가 **400** 이다.
 * 담기·교체 경로가 이미 막고 있지만(addProblems 의 have 집합 · putInSlot 의 중복 검사),
 * 그 둘은 String 으로 견주고 요청은 Number 로 나간다 — '12' 와 12 가 섞이면 화면에서는 다른 행인데
 * 서버에는 같은 값 둘이 된다. 그래서 **나가는 값 그대로** 한 번 더 센다.
 * 돌려주는 것은 두 번 이상 나온 problem_id 배열(없으면 빈 배열).
 */
function duplicateIds(items) {
  const seen = new Set();
  const dup = new Set();
  items.forEach((o) => {
    if (seen.has(o.problem_id)) dup.add(o.problem_id);
    else seen.add(o.problem_id);
  });
  return Array.from(dup);
}

/**
 * assignLabel — [저장하고 배정] 버튼의 문구. **기본값이 곧 시험지(/exam-papers)의 현행 문구**라
 * 그쪽 화면은 이 인자가 생겨도 한 글자도 달라지지 않는다.
 * 학습(/students-hub)에서 이 창을 열 때만 갈아 끼운다 — 그 자리는 학생이 이미 정해져 있어
 * 「배정」이 누구에게인지 화면에 남지 않으면 눌러 보고 나서야 알게 된다.
 * (같은 화면의 AITrackerContent 가 useV2Preview 로 쓰는 방식이다 — 부품을 fork 하지 않고
 *  쓰는 쪽에서만 켠다. 이 창은 2600줄이라 사본을 뜨면 그 순간 두 벌이 갈린다.)
 * cancelLabel·closeConfirmMessage·closeConfirmText도 같은 원칙이다. 학생 허브에서만
 * 「시험지 선택으로」 복귀 맥락을 밝히고, 독립 시험지 화면의 기존 문구는 기본값으로 보존한다.
 */
function PaperComposeModal({
  isOpen,
  paper,
  onClose,
  onSaved,
  onAssignRequested,
  assignLabel = '저장하고 배정',
  assignmentTargetName = '',
  assignPrimary = false,
  cancelLabel = '취소',
  closeConfirmMessage = '닫으면 지금까지 편집한 내용이 사라집니다.',
  closeConfirmText = '그래도 닫기',
}) {
  const { getAccessToken, userId } = useAuth();
  const paperId = paper == null ? null : (paper.paper_id ?? paper.id ?? null);

  // ── 시험지 본문 ──
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [rows, setRows] = useState([]);
  const [itemsUnknown, setItemsUnknown] = useState(false);
  // 뒤늦게 문항이 붙은 판 — 사용자가 이미 담아 둔 문항이 있으면 덮기 전에 확인을 받는다
  const [lateItems, setLateItems] = useState(null);
  const [createdId, setCreatedId] = useState(null); // 새 시험지를 만든 뒤의 두 번째 저장은 PATCH 다
  const [newPrintContext, setNewPrintContext] = useState(createPrintDraftContext);
  const seedRef = useRef({ title: '', description: '', rows: [] });
  const seededPaper = useRef(null); // 통째로 심은 시험지 — 열려 있는 동안 한 번뿐이다
  const handledPlate = useRef(null); // 판단이 끝난 판(id-revision-문항유무) — 같은 판을 두 번 처리하지 않는다

  // ── 좌측 검색 ──
  const [form, setForm] = useState(EMPTY_FORM);
  const formRef = useRef(form);
  formRef.current = form;
  // 개념 사전. null 은 「아직 없음/못 받음」이고 화면이 그렇게 말한다 — 빈 배열로 두면
  // 「개념이 하나도 없다」와 구분되지 않는다(ConceptPicker 가 같은 이유로 다시 시도를 준다).
  const [conceptDict, setConceptDict] = useState(null);
  const [dictLoading, setDictLoading] = useState(false);
  const [dictKey, setDictKey] = useState(0); // 다시 시도용 — 올리면 아래 effect 가 다시 돈다
  const [results, setResults] = useState({ items: [], page: 1, totalPages: 1, totalItems: 0 });
  const [picked, setPicked] = useState([]); // 페이지를 넘겨도 유지되는 선택(담기 전까지)
  // 결과 머리 [전체 선택]으로 고른 묶음 — { key: 그 결과를 만든 검색 키, ids: [문자열 problem_id] }.
  // 체크칸의 켜짐은 「이 묶음이 아직 다 골라져 있는가」로 판정한다 — 지금 쪽만 봐서는 위 100개가 다 골라졌는지 알 수 없다
  const [bulk, setBulk] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const bulkBusyRef = useRef(false); // 이중 클릭 차단 — 100개를 받는 동안 또 누르면 왕복이 겹친다
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  // 실패 자리에 [다시 시도] 대신 [OR로 다시 검색]을 세우는가 — 이 서버에 AND(모두 포함) 검색이 아직 없을 때만(오너 결정 D4).
  // 오류 글과 짝으로만 읽힌다(searchError 가 있을 때만 그 자리가 그려진다) — 오류 글을 세우는 자리마다 함께 정한다.
  const [searchErrorOffersOr, setSearchErrorOffersOr] = useState(false);
  const searchSeq = useRef(0);
  // ── 강의 칸 ── 줄 하나를 고르면 그 조건식이 검색을 몬다(본문 검색 POST). 그동안 폼은 감추고 싣지 않는다 —
  // 폼을 「결과 좁히기」로 겹치던 칸은 오너 지시로 걷었다(2026-09-14). 보이지 않는 필터가 결과를 좁히면 안 된다.
  // 식은 ref 로도 붙잡는다 — load() 를 부르는 자리(검색·페이지·다시 시도·분석 필요만·창 열기)가 다섯이라 자리마다
  // 식을 넘기면 하나를 빠뜨리는 순간 그 페이지만 조용히 주소줄 검색으로 샌다. load 가 await 전에 한 번 읽는다.
  // sylOpen — 강의 칸이 펼쳐져 있는가. 창을 열면 펼쳐져 있고 손잡이로 접는다(오너 2026-09-14 「기본이 펼쳐지는 형태」)
  const [sylOpen, setSylOpen] = useState(true);
  const [sylPick, setSylPick] = useState(null); // { syllabusId, syllabusName, nodeId, name, path[], query }
  const [sylWarnings, setSylWarnings] = useState([]); // 성공한 검색의 없는 참조 경고 — 해당 식의 match와 함께 표시
  const sylExprRef = useRef(null);
  const [sylQuery, setSylQuery] = useState(null);
  const syllabusDrafts = useRef(new Map());
  const [labelNames, setLabelNames] = useState(readLabelNames);
  const [successfulSearch, setSuccessfulSearch] = useState(null);
  const successfulSearchRef = useRef(null); // 입력 변경과 별개로, 지금 표시하는 결과의 본문 변환을 식별한다
  const failedSearchRef = useRef(null);
  const inflightSearchRef = useRef(null);
  const resultListRef = useRef(null);
  const currentSearchKey = composeSearchKey(sylQuery, form);
  const staleSearch = successfulSearch != null && successfulSearch.key !== currentSearchKey;
  const searchCheck = sylQuery == null ? null : inspectExpr(sylQuery, { mode: 'search' });
  const searchAllowed = sylQuery == null || searchCheck.ok;
  const searchPageBlocked = useRef(false);
  searchPageBlocked.current = searching || staleSearch || !searchAllowed;
  const rememberLabels = (pairs) => { rememberLabelNames(pairs); setLabelNames(readLabelNames()); };
  // 입력은 검색하지 않는다. 진행 중인 옛 요청만 무효화하여 늦은 결과가 새 입력에 붙지 않게 한다.
  const invalidateSearch = () => {
    searchSeq.current += 1;
    inflightSearchRef.current = null;
    setSearching(false);
    setSearchError(null);
  };
  const changeForm = (next) => {
    formRef.current = next;
    setForm(next);
    invalidateSearch();
  };
  const changeSyllabusQuery = (next, pick = sylPick) => {
    sylExprRef.current = cloneQuery(next);
    if (pick) syllabusDrafts.current.set(`${pick.syllabusId}:${pick.nodeId}`, sylExprRef.current);
    setSylQuery(sylExprRef.current);
    invalidateSearch();
  };
  const seenRef = useRef(new Map()); // 넘긴 페이지의 선택도 미리보기·난이도·라벨을 들고 갈 수 있게
  // 카드에 그릴 **조판 본문** — id → HTML. 키는 **문자열로 굳힌 problem_id** 다(응답은 정수로 준다).
  // 재료는 검색 응답이 with_body=1 로 함께 실어 준 body_lexical 이고(계약 §9), load() 가 그 자리에서
  // HTML 로 굳혀 넣는다 — **본문을 위한 별도 왕복이 없다.** 이 지도가 비는 것은
  // ① 본문이 없는 문항(body_lexical:null → 키 자체가 안 온다) ② Lexical→HTML 변환 실패, 둘뿐이다.
  // 어느 경우든 그 카드만 평문 요약(preview 140자)으로 남고 목록은 그대로 선다.
  const [searchBodies, setSearchBodies] = useState({});

  // ── 가운데 '담은 문항'의 조판 본문 ────────────────────────────────────────
  // id(문자열) → HTML. 좌측 검색 카드와 **같은 얼굴**이어야 해서 변환기도 같다(toBodyHtmlMap).
  // 받는 길은 **v2 한 길**이다(fetchProblemBodiesV2 — 계약 §9 의 problem_ids + with_body=1).
  // 우측 지면의 contentMap 과 헷갈리지 마라: 그쪽은 v1(/api/problems/print/page)이고 지면 조판용이라
  // 값의 모양도 다르다({content, ok}). 여기는 문자열 HTML 하나다.
  const [rowBodies, setRowBodies] = useState({});
  // 이미 처리한 id — 물어봤든(성공·실패) 검색 결과에서 물려받았든 자국을 남긴다. 순서만 바꿔도
  // rows 는 새 배열이 되므로 이 자국이 없으면 행을 위아래로 옮길 때마다 같은 요청이 다시 난다
  // (좌측 seenRef · 우측 contentPending 과 같은 관례).
  const rowBodySeen = useRef(new Set());
  // id(문자열) → 검색 item 한 벌. **본문과 같은 응답에서 나온다**(with_body=1 이 얹힌 그 호출).
  // 여기서 꺼내 쓰는 것은 개념(concept_names·concept_ids)이다 — 서버가 준 시험지를 그대로 연
  // 경우 상세가 개념을 안 실어 주기 때문이고, 그 한 값을 위해 왕복을 더 내지 않으려고
  // 본문 트랙이 받아 온 item 을 통째로 들고 있는다.
  const [rowItems, setRowItems] = useState({});

  // ── 상세 다시 읽기(문항을 못 알아봤을 때의 복구) ──
  const [detail, setDetail] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const [repairError, setRepairError] = useState(null);
  const repairSeq = useRef(0);

  // ── 그 밖의 화면 상태 ──
  const [busy, setBusy] = useState(null); // 'save' | 'assign'
  const submitRef = useRef(false); // 상태 반영 전 같은 tick의 연속 확정도 쓰기 한 번으로 가둔다
  const [toast, setToast] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmAssign, setConfirmAssign] = useState(null); // { title, count } · 학생 문맥에서만
  // 좁은 폭에서 지면이 물러나 앉는 서랍. true 면 열려 있다.
  // 지면이 열로 서 있으면 이 값이 화면에 닿지 않는다 — 그래도 켜 두는 편이
  // 낫다: 폭을 다시 좁혔을 때 보고 있던 상태가 남는다.
  const [paperOpen, setPaperOpen] = useState(false);
  // 지면이 세 번째 열로 서는지 서랍인지 — 열 상자의 실제 폭과 강의 칸이 함께 정한다(paperColumn.composePaperAsColumn).
  // 강의 칸을 접으면 종전 xl(1280) 경계 그대로, 펴면 그 320px 만큼 넓은 화면(약 1607↑)에서 네 칸이 다 선다(오너 2026-09-15).
  // 미디어 쿼리가 아니라 폭을 재는 이유: 창이 이제 화면 폭을 따라 늘고(98vw), 강의 칸이 그 안에서 폭을 먹는다.
  const [rowRef, rowPx] = useBoxWidth();
  const paperAsColumn = composePaperAsColumn(rowPx, sylOpen);
  // 목록과 지면이 같은 ID를 선택한다. 선택 자체는 문항 배열이나 dirty를 바꾸지 않는다.
  const [selection, setSelection] = useState(null);
  const selectedId = selection?.id ?? null;
  const selectedFromPaper = selection?.fromPaper === true;
  // 새 선택은 본문을 펼친다. 순서만 바뀌면 사용자가 정한 펼침 상태를 유지한다.
  const setSelectedId = useCallback((id, fromPaper = false) => setSelection((current) => {
    if (id == null) return null;
    return String(current?.id) === String(id)
      ? { ...current, fromPaper } : { id, expanded: true, fromPaper };
  }), []);
  const listHeadingRef = useRef(null);
  const [actionMode, setActionMode] = useState(null); // null | replace | swap | move
  const [candidateId, setCandidateId] = useState(null);
  const focusRowRef = useRef(null);
  const revealRowRef = useRef(null);
  const cancelEditing = useCallback(() => {
    setActionMode(null);
    setCandidateId(null);
  }, []);
  const resetSelection = useCallback(() => {
    setSelectedId(null);
    cancelEditing();
    focusRowRef.current = null;
    revealRowRef.current = null;
  }, [cancelEditing, setSelectedId]);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true; // StrictMode 의 mount→cleanup→mount 에서 false 로 굳지 않게 되살린다
    return () => { aliveRef.current = false; };
  }, []);

  // 뒤늦게 온 판을 판단할 때 '지금 담긴 문항'이 필요하다. rows 를 의존성에 넣으면 행을 고칠
  // 때마다 그 판단이 다시 도므로 값만 훔쳐 본다(이 effect 가 아래 것보다 먼저 선언돼야 최신이다).
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; });

  // 지면 머리글 제목을 묵히지 않고 곧바로 넣는 통로 — 지면 훅(usePaperSheet)은 아래에서 세워지므로 ref 로 잇는다
  const sheetTitleNowRef = useRef(null);
  const seed = useCallback((p) => {
    setCreatedId(null);
    if (p?.paper_id == null && p?.id == null) setNewPrintContext(createPrintDraftContext());
    const t = p?.title ?? '';
    const d = p?.description ?? '';
    const items = pickItems(p);
    setItemsUnknown(p != null && items === null);
    const r = toRows(items);
    setTitle(t);
    setDescription(d);
    setRows(r);
    rowsRef.current = r;
    resetSelection();
    sheetTitleNowRef.current?.(t.trim()); // 지면 머리글은 타이핑만 묵힌다 — 열자마자 제목이 비어 보이지 않게
    seedRef.current = { title: t, description: d, rows: r };
  }, [resetSelection]);

  /** 뒤늦게 온 서버 문항만 받는다 — 제목·설명은 사용자가 만졌을 수 있으니 기준선까지 그대로 둔다 */
  const adoptItems = useCallback((p) => {
    const r = toRows(pickItems(p));
    setRows(r);
    rowsRef.current = r;
    resetSelection();
    setItemsUnknown(false);
    // 서버가 준 문항은 사용자의 변경이 아니다 — 기준선도 함께 옮겨야 dirty 가 거짓말을 하지 않는다
    seedRef.current = { ...seedRef.current, rows: r };
    setLateItems(null);
  }, [resetSelection]);

  // 목록 행(GET /papers 요약)에는 문항이 없다 — 열자마자 상세를 다시 읽는 것이 예외가 아니라
  // 정상 경로다. 그 응답이 도착했을 때 통째로 심으면 그 사이에 친 제목·담은 문항이 서버 값으로
  // 덮이고, 기준선까지 갈아 치워 경고조차 남지 않는다.
  // 그래서 통째로 심는 것은 열려 있는 동안 시험지당 한 번뿐이다. 뒤늦게 온 판은 비어 있던
  // 자리(문항)만 채우고, 사용자가 이미 담아 둔 문항이 있으면 확인 바로 물어본다.
  useEffect(() => {
    if (!isOpen) {
      seededPaper.current = null;
      handledPlate.current = null;
      return;
    }
    const fresh = detail != null && String(detail.paper_id ?? detail.id) === String(paperId) ? detail : paper;
    const items = pickItems(fresh);
    const paperKey = fresh == null ? 'new' : String(fresh.paper_id ?? fresh.id);
    const plate = `${paperKey}-${fresh?.revision ?? 0}-${items === null ? 'noitems' : 'items'}`;
    if (handledPlate.current === plate) return;
    handledPlate.current = plate;

    if (seededPaper.current !== paperKey) {
      seededPaper.current = paperKey;
      setLateItems(null);
      seed(fresh);
      setToast(null);
      return;
    }
    if (items === null) return; // 여전히 문항을 못 알아본다 — 채울 것이 없다
    if (rowsRef.current.length > 0) {
      setLateItems(fresh);
      return;
    }
    adoptItems(fresh);
  }, [isOpen, paper, paperId, detail, seed, adoptItems]);

  const repair = useCallback(async () => {
    if (paperId == null) return;
    const seq = (repairSeq.current += 1);
    setRepairing(true);
    setRepairError(null);
    try {
      const d = await fetchPaper(paperId, getAccessToken);
      if (!aliveRef.current || seq !== repairSeq.current) return;
      setDetail(d);
    } catch (e) {
      if (!aliveRef.current || seq !== repairSeq.current) return;
      setRepairError(e.message || '시험지를 다시 읽지 못했습니다.');
    } finally {
      // 로딩 플래그는 자기 순번으로 잰다 — 무효화된 순번이 끄면 스피너가 영구히 고착된다
      if (aliveRef.current && seq === repairSeq.current) setRepairing(false);
    }
  }, [paperId, getAccessToken]);

  // 목록 행처럼 문항이 안 실린 객체로 열렸을 수 있다 — 한 번만 상세를 다시 읽어 고쳐 본다.
  // (detail 이 이미 있으면 다시 부르지 않는다. 그래도 못 알아보면 저장에서 items 를 뺀다.)
  useEffect(() => {
    if (!isOpen || paperId == null || !itemsUnknown || detail != null) return;
    repair();
  }, [isOpen, paperId, itemsUnknown, detail, repair]);

  const load = useCallback(async (f, page) => {
    const expr = cloneQuery(sylExprRef.current);
    const check = expr == null ? null : inspectExpr(expr, { mode: 'search' });
    if (check && !check.ok) { setSearchErrorOffersOr(false); setSearchError(check.error.message); return; }
    const sentForm = { ...f, conceptIds: [...f.conceptIds] };
    const key = composeSearchKey(expr, sentForm);
    const requestKey = `${key}:${page}`;
    if (inflightSearchRef.current?.key === requestKey) return;
    const seq = ++searchSeq.current;
    inflightSearchRef.current = { key: requestKey, seq };
    const live = () => aliveRef.current && seq === searchSeq.current && key === composeSearchKey(sylExprRef.current, formRef.current);
    // 개념 AND 로 찾는가 — 실패했을 때 「배포 전 서버라 AND 가 없다」를 가려내려고 여기서도 본다(갈래 고르기는 searchComposePage)
    const allExpr = conceptAllExprOf(expr, sentForm);
    setSearching(true);
    setSearchError(null);
    try {
      const d = await searchComposePage({ expr: expr ? check.normalized : null, form: sentForm, page }, getAccessToken);
      if (!live()) return;
      failedSearchRef.current = null;
      setSylWarnings(expr ? d.warnings || [] : []);
      d.items.forEach((it) => seenRef.current.set(String(it.problem_id), it));
      const snapshot = { key, expr: check?.normalized || null, form: sentForm, page, at: Date.now() };
      successfulSearchRef.current = snapshot;
      setResults(d);
      setSuccessfulSearch(snapshot);
      setSearchBodies({}); // 새 목록에 이전 본문을 붙이지 않는다
      setSearching(false);
      if (inflightSearchRef.current?.seq === seq) inflightSearchRef.current = null;
      if (resultListRef.current) resultListRef.current.scrollTop = 0;
      let bodies = {};
      try { bodies = await toSearchBodyMap(d.items); }
      catch (e) { console.error('검색 결과 본문 변환기 로드 실패:', e); }
      // 조건을 고치거나 다음 검색이 실패해도 이 결과는 화면에 남는다. 새 성공 결과에만 옛 본문을 붙이지 않는다.
      if (aliveRef.current && successfulSearchRef.current === snapshot) setSearchBodies(bodies);
    } catch (e) {
      if (!live()) return;
      failedSearchRef.current = { key, page };
      // 이 서버에 본문 검색이 아직 없다(배포 전) — AND 로 찾던 중이면 조건 탓이 아니니 OR 로 다시 찾는 길을 준다(D4)
      const andUnavailable = allExpr != null && isPredeployError(e);
      setSearchErrorOffersOr(andUnavailable);
      setSearchError(andUnavailable
        ? CONCEPT_ALL_UNAVAILABLE_MSG
        : (expr || allExpr) ? describeSyllabusError(e) : (e.message || '문제를 불러오지 못했습니다.'));
      // 실패는 마지막 성공 결과와 담은 문항을 없애지 않는다.
    } finally {
      if (seq === searchSeq.current) {
        setSearching(false);
        if (inflightSearchRef.current?.seq === seq) inflightSearchRef.current = null;
      }
    }
  }, [getAccessToken]);

  // 개념 사전은 창이 열릴 때 한 번 읽는다. 검색과 따로 두는 이유는 수명이 다르기 때문이다 —
  // 목록은 조건마다 다시 오지만 사전은 717개짜리 고정 카탈로그다(모듈 캐시 5분).
  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    setDictLoading(true);
    Promise.resolve(fetchConceptDictV2(getAccessToken))
      .then((d) => { if (alive) setConceptDict(d); })
      .finally(() => { if (alive) setDictLoading(false); });
    return () => { alive = false; };
  }, [isOpen, getAccessToken, dictKey]);

  // 열릴 때 초기화 + 전체 1페이지(필터를 안 주면 전체가 나온다 — 계약 §2)
  useEffect(() => {
    if (!isOpen) return undefined;
    formRef.current = EMPTY_FORM;
    setForm(EMPTY_FORM);
    setPicked([]);
    setBulk(null); // 고른 것과 짝이다 — 고른 것을 비우면 「무엇을 통째로 골랐나」의 기억도 함께 비운다
    // 지난번 결과를 비운다 — 안 비우면 조건은 초기화됐는데 옛 목록이 그대로 보이고,
    // 스켈레톤도 (items 가 비어 있지 않아) 뜨지 않는다.
    setResults({ items: [], page: 1, totalPages: 1, totalItems: 0 });
    setSearchBodies({}); // 결과와 짝으로 비운다 — 본문 지도만 남으면 옛 목록의 본문을 들고 있게 된다
    seenRef.current = new Map();
    setPaperOpen(false); // 서랍은 접힌 채로 시작한다 — 창을 열면 하는 일은 먼저 고르는 것이다
    // 강의 선택도 함께 푼다 — 초기화가 이긴다(판 2 §4-4). 마지막에 쓴 줄은 강의 칸 머리에 한 줄로 남는다.
    // 강의 칸은 펼친 채로 연다(오너 2026-09-14 「기본이 펼쳐지는 형태」) — 지난번에 접었어도 다시 편다
    sylExprRef.current = null;
    setSylQuery(null);
    setSylPick(null);
    setSylOpen(true);
    setSylWarnings([]);
    setSuccessfulSearch(null);
    successfulSearchRef.current = null;
    failedSearchRef.current = null;
    inflightSearchRef.current = null;
    syllabusDrafts.current.clear();
    load(EMPTY_FORM, 1);
    return () => {
      searchSeq.current += 1;
      successfulSearchRef.current = null; // 닫는 중에 도착할 검색 응답과 본문 변환을 함께 무시
    };
  }, [isOpen, load]);

  // 닫히면 다음 열기를 위해 지운다 — 같은 시험지를 다시 열면 서버 상태에서 시작해야 한다.
  // 본문 캐시(contentMap)만 남긴다 — problem_id 로 결정되는 값이라 시험지가 바뀌어도 유효하고,
  // 지우면 같은 문제를 다시 담을 때마다 지면이 '불러오는 중'으로 되돌아간다.
  useEffect(() => {
    if (isOpen) return;
    setDetail(null);
    setRepairError(null);
    setCreatedId(null);
    setLateItems(null);
    setConfirmClose(false);
    resetSelection();
    // 지면의 전체화면·손 배율은 지면 훅(usePaperSheet)이 창이 닫힐 때 스스로 내린다
  }, [isOpen, resetSelection]);

  // ── 담은 문항 본문 읽기(v2) ────────────────────────────────────────────────
  // 아직 모르는 id 만 모아 **한 번에** 묻는다(fetchProblemBodiesV2 — 100개씩 끊어 병렬).
  // 두 번 묻지 않게 막는 자는 rowBodySeen 이다. 결과가 없는 id 까지 자국을 남기는 이유는,
  // 본문이 없는 문항(body_lexical:null)과 변환 실패가 rowBodies 에 흔적을 남기지 않아 —
  // 남기면 평문 폴백이 꺼진다 — 자국이 없으면 렌더마다 같은 요청이 다시 나가기 때문이다.
  // 그 대가로 **한 번 실패한 id 는 이 창이 열려 있는 동안 다시 묻지 않는다**(그 행은 평문으로 버틴다).
  // problem_id 가 없는 행은 본문을 물어볼 수도 없다 — 요청에 'undefined' 를 실어 보내지 않는다
  const askableIds = rows.map((r) => (r.problem_id == null ? '' : String(r.problem_id))).filter(Boolean);
  const bodyAskKey = askableIds.join(',');
  useEffect(() => {
    const ids = bodyAskKey === '' ? [] : bodyAskKey.split(',');
    const todo = ids.filter((id) => !rowBodySeen.current.has(id));
    if (todo.length === 0) return;
    // 검색 결과가 이미 들고 온 본문은 **다시 묻지 않고 물려받는다**(같은 응답에 실려 온 같은 값이다).
    // 지금 물려받아 두는 것이 요점이다 — 검색 페이지를 넘기면 searchBodies 는 옛 목록과 짝으로
    // 비워지므로, 그때 가서 읽으면 이미 없다.
    const inherit = {};
    const inheritItems = {};
    const ask = [];
    todo.forEach((id) => {
      if (searchBodies[id] != null) {
        inherit[id] = searchBodies[id];
        // 개념도 같은 검색 응답에 있었다 — 본문만 물려받고 배지를 위해 다시 묻는 것은 낭비다
        const it = seenRef.current.get(id);
        if (it) inheritItems[id] = it;
      } else ask.push(id);
    });
    // StrictMode 의 두 번째 호출까지 막으려면 자국은 요청 **전에** 남긴다
    todo.forEach((id) => rowBodySeen.current.add(id));
    if (Object.keys(inherit).length > 0) setRowBodies((prev) => ({ ...prev, ...inherit }));
    if (Object.keys(inheritItems).length > 0) setRowItems((prev) => ({ ...prev, ...inheritItems }));
    if (ask.length === 0) return;
    (async () => {
      // 전부 실패하면 null 이 온다 — 던지지 않는다(problemBodyV2 의 규약). 목록은 그대로 서고
      // 그 행들만 평문 preview 로 남는다.
      const items = await fetchProblemItemsV2(ask, getAccessToken, { withBody: true });
      if (!aliveRef.current || items == null) return;
      // 개념 배지가 읽을 자리를 먼저 채운다 — 본문 변환이 실패해도 배지는 서야 한다
      setRowItems((prev) => ({ ...prev, ...items }));
      // 본문이 없는 문항(body_lexical:null)은 키를 넣지 않는다 — 그 행은 평문 요약으로 남는다
      const raw = {};
      Object.entries(items).forEach(([key, it]) => {
        if (it?.body_lexical != null) raw[key] = it.body_lexical;
      });
      if (Object.keys(raw).length === 0) return;
      let html = {};
      try {
        html = await toBodyHtmlMap(raw);
      } catch (e) {
        // 변환기(lexical·katex)를 못 받은 경우 — 여기서 던지면 창이 통째로 넘어진다
        console.error('담은 문항 본문 변환기 로드 실패:', e);
        return;
      }
      if (!aliveRef.current || Object.keys(html).length === 0) return;
      // id 를 키로 합쳐 넣는다 — 늦게 온 응답이 먼저 온 것을 덮을 수 없다(contentMap 과 같은 셈)
      setRowBodies((prev) => ({ ...prev, ...html }));
    })();
  }, [bodyAskKey, searchBodies, getAccessToken]);

  /**
   * ── 매스컨셉 배지 ─────────────────────────────────────────────────────────
   * 순번(order)·유형(concept_type)은 **개념 자체의 속성**이라 문제 응답에 없고 사전(계약 §8)에만
   * 있다. 그 둘이 없으면 배지는 이름만 남는다(구형과 같은 조건부 구조).
   *
   * `matchConcept` 을 쓰지 않고 Map 을 만드는 이유는 문항 편집기(PaperItemsEditor)와 같다 —
   * 그쪽은 717개를 `find` 로 훑고 비교마다 `String()` 을 만든다. 검색 20건 + 담은 문항 수십 개가
   * 한 화면에 서는 자리라 한 번 그릴 때만 수십만 번 비교가 된다.
   */
  const dictIndex = useMemo(() => {
    if (!conceptDict?.length) return null;
    const m = new Map();
    conceptDict.forEach((c) => { if (c?.id != null) m.set(String(c.id), c); });
    return m;
  }, [conceptDict]);

  /** [{id,name}] → 사전으로 순번·유형을 채운 배지 재료. 사전이 없으면 이름만 남는다 */
  const withDict = useCallback((pairs) => {
    if (!Array.isArray(pairs) || pairs.length === 0) return [];
    if (dictIndex == null) return pairs.filter((c) => c.name).map((c) => ({ ...c, order: null, type: null }));
    return pairs.map((c) => {
      const m = c.id == null ? null : dictIndex.get(String(c.id));
      return {
        ...c,
        name: c.name || m?.concept_name || m?.name || '',
        order: m?.order ?? null,
        type: m?.concept_type ?? null,
      };
    }).filter((c) => c.name);
  }, [dictIndex]);

  /**
   * 담은 문항 한 줄의 개념 — 담을 때 실어 둔 것이 먼저고, 없으면 본문과 함께 받아 둔 item 에서
   * 꺼낸다. 서버가 준 시험지를 그대로 연 경우가 후자다(상세가 개념을 안 실어 준다).
   */
  const rowConcepts = useCallback((r) => {
    const key = r?.problem_id == null ? '' : String(r.problem_id);
    const pairs = r?.concepts?.length ? r.concepts : conceptPairsOf(rowItems[key]);
    return withDict(pairs);
  }, [rowItems, withDict]);

  /**
   * 개념 설명 창 — 배지를 누르면 그 개념 하나를 v2 로 받아 공용 모달에 넘긴다
   * (오너 지시 2026-08-31 「배지를 선택하면 매스컨셉 상세 모달이 실행되도록」).
   *
   * 사전(conceptDict)을 그대로 넘기지 않는 이유는 **설명이 그 안에 없기 때문**이다 —
   * 717개 전부에 설명을 실으면 캐시가 무거워져 계약이 `with_description=1` 로 갈라 놓았다.
   * 그래서 누른 그때 한 번 받는다. 문항 편집기(PaperItemsEditor)와 같은 이음매다.
   */
  const [conceptDetail, setConceptDetail] = useState(null); // { loading, data } · null 이면 닫힘
  const openConcept = useCallback(async (conceptId) => {
    if (conceptId == null) return;
    setConceptDetail({ loading: true, data: [] });
    const list = await fetchConceptDetailV2([conceptId], getAccessToken);
    if (!aliveRef.current) return;
    // **빈 창을 띄우지 않는다** — 못 받았는데 창만 열리면 「설명이 없는 개념」으로 읽힌다
    if (list == null) {
      setConceptDetail(null);
      setToast({ type: 'error', text: '개념 설명을 불러오지 못했습니다.' });
      return;
    }
    setConceptDetail({ loading: false, data: list });
  }, [getAccessToken]);

  /** 담은 문항 한 줄의 조판 본문. 방금 검색이 들고 온 것이 있으면 그것도 같은 값이다 */
  const rowBodyHtml = (id) => {
    const key = id == null ? '' : String(id);
    return rowBodies[key] ?? searchBodies[key] ?? null;
  };

  // 809행대에서 올려 왔다 — 아래 지면 머리글이 렌더 중에 읽는다(그 자리에 두면 TDZ 다)
  const effectiveId = paperId ?? createdId;
  const printContext = useMemo(() => effectiveId == null ? newPrintContext : { kind: 'paper', id: String(effectiveId) },
    [effectiveId, newPrintContext]);

  // ── 우측 지면(v4) — 공용 훅(usePaperSheet). 본문·측정·배치·정답·머리글·배율·전체화면 미리보기가 거기 한 벌로 있다 ──
  // problemIds 는 rows 에서 매 렌더 새로 만든다 — 훅이 id 문자열로 재서 순서가 진짜로 바뀔 때만 지면을 다시 짠다.
  const sheet = usePaperSheet({
    active: isOpen,
    problemIds: rows.map((r) => r.problem_id),
    title,
    examLabel: effectiveId == null ? '미저장' : String(effectiveId),
    getAccessToken,
    printContext,
  });
  const {
    previewBoxRef, laid, problemPages, previewOpen, scale: paperScale,
  } = sheet;
  sheetTitleNowRef.current = sheet.setTitleNow;

  const isNew = effectiveId == null;   // effectiveId 는 위 지면 훅 앞에서 선언한다(지면 머리글이 읽는다)
  const dirty = title !== seedRef.current.title
    || description !== seedRef.current.description
    || JSON.stringify(toPayload(rows)) !== JSON.stringify(toPayload(seedRef.current.rows));

  /* 문제 번호 링크가 이 창을 떠나기 전에 물어볼 수 있게 하는 가드(ProblemNavGuard).
     페이지의 가드는 이 창의 편집을 모른다 — 창은 onDirtyChange 로 알리지 않기 때문이다. */
  const dirtyRef = useRef(false);
  const confirmLeave = useCallback(() => {
    if (!dirtyRef.current) return true;
    // eslint-disable-next-line no-alert
    return window.confirm('저장하지 않은 시험지 편집이 있습니다. 버리고 그 문제로 이동할까요?');
  }, []);

  const already = new Set(rows.map((r) => String(r.problem_id)));

  const canEditNow = () => isOpen && busy == null && !submitRef.current
    && !previewOpen && !confirmClose && confirmAssign == null && conceptDetail == null;
  const actionRowsRef = useRef(null);
  const commitRows = (next) => {
    if (!canEditNow() || next === rowsRef.current) return false;
    rowsRef.current = next;
    setRows(next);
    cancelEditing();
    return true;
  };
  const selectAfterChange = (id, focus = false) => {
    setSelectedId(id);
    revealRowRef.current = id;
    if (focus) focusRowRef.current = { id };
  };
  const move = (id, delta) => {
    if (!canEditNow()) return;
    const current = rowsRef.current;
    const from = uniqueRowIndex(current, id);
    if (from < 0) return;
    if (commitRows(movePaperRow(current, id, from + delta))) selectAfterChange(id);
  };
  const removeAt = (idx) => {
    if (!canEditNow()) return;
    const current = rowsRef.current;
    const removed = current[idx];
    if (!removed) return;
    const next = current.filter((_, i) => i !== idx);
    if (!commitRows(next)) return;
    if (String(selectedId) === String(removed.problem_id)) setSelectedId(null);
    focusRowRef.current = { id: next[Math.min(idx, next.length - 1)]?.problem_id ?? null };
  };

  /**
   * 담기 — 고른 화면이 보여 주던 미리보기·난이도·라벨을 함께 들고 온다(저장 전에도 읽히게).
   *
   * at 은 **꽂을 자리**다. null 이면 맨 뒤([담기] 버튼·체크박스 다중 선택의 셈 그대로),
   * 숫자면 그 자리에 끼워 넣고 뒤엣것들이 한 칸씩 밀린다(끌어다 놓기).
   * 두 길이 행을 **한 벌로** 만드는 것이 요점이다 — 두 벌로 두면 한쪽만 preview·난이도·라벨을
   * 빠뜨려도 빌드는 통과하고, 그 행만 조용히 빈 카드가 된다.
   *
   * 이미 담긴 id 를 거르는 것도 여기 한 곳이다(계약 §1-3 — 같은 problem_id 가 둘이면 저장이 400).
   */
  const insertProblems = (ids, at = null) => {
    if (!canEditNow()) return;
    const current = rowsRef.current;
    const have = new Set(current.map((r) => String(r.problem_id)));
    const added = ids
      .filter((id) => {
        if (id == null || have.has(String(id))) return false;
        have.add(String(id));
        return true;
      })
      .map((id) => {
        const it = seenRef.current.get(String(id));
        return {
          problem_id: id,
          points: '',
          preview: it?.preview ?? '',
          difficulty: it?.difficulty ?? null,
          labels: labelText(it?.labels),
          // 검색 응답에 이미 있던 값이다 — 지금 실어 두지 않으면 페이지를 넘긴 뒤
          // 이 행의 배지를 위해 같은 문제를 다시 묻게 된다
          concepts: conceptPairsOf(it),
        };
      });
    if (added.length === 0) {
      setToast({ type: 'info', text: '고른 문제가 모두 이미 담겨 있습니다.' });
      return;
    }
    // 자리는 한 번 더 가둔다 — 놓는 사이에 행이 빠졌으면(빼기·서버 값 다시 읽기) 범위를 넘는다
    const pos = at == null ? current.length : Math.max(0, Math.min(at, current.length));
    const next = current.slice();
    next.splice(pos, 0, ...added);
    if (!commitRows(next)) return;
    setPicked((p) => p.filter((id) => !added.some((a) => String(a.problem_id) === String(id))));
    setToast({
      type: 'info',
      text: at == null
        ? `${added.length}개를 뒤에 담았습니다 — 저장해야 반영됩니다.`
        : `${added.length}개를 ${pos + 1}번 자리에 담았습니다 — 저장해야 반영됩니다.`,
    });
  };
  /** [담기] 버튼·다중 선택이 부르는 얇은 껍데기 — 늘 맨 뒤다 */
  const addProblems = (ids) => insertProblems(ids, null);

  const toggle = (id) => {
    if (!canEditNow() || already.has(String(id))) return;
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };

  const selectedIdx = uniqueRowIndex(rows, selectedId);
  const replacing = actionMode === 'replace' && selectedIdx >= 0;

  // ── 결과 머리 [전체 선택] ──────────────────────────────────────────────────
  // 학생 배포 창(AssignPanel)의 「검색 결과 전체 선택」과 같은 동작이다 — 다른 점은 상한 하나다:
  // 결과가 PICK_TOP_LIMIT 를 넘으면 **맨 위부터 그만큼만** 고른다(오너 결정 2026-10-09).
  // 시험지 한 장에 수만 문항을 담을 일은 없고, 상한을 넘었다고 체크칸을 꺼 버리면 창을 처음 열었을 때(전체 결과)
  // 늘 쓸 수 없는 단추가 된다.
  const pickedKeys = useMemo(() => new Set(picked.map(String)), [picked]);
  // 묶음 중 아직 고를 수 있는 것 — 그사이 담은 문제는 뺀다(담으면 picked 에서도 빠지므로, 빼지 않으면 체크가 영영 꺼진다).
  // 다른 조건의 결과가 서 있으면 그 묶음은 지금 결과에 대한 말이 아니다 — 체크만 꺼지고 **골라 둔 문제는 그대로다**
  // (강의 두 줄을 이어 고를 수 있게). 쪽만 넘긴 것이면 키가 같아 그대로 산다.
  const bulkIds = bulk != null && successfulSearch != null && bulk.key === successfulSearch.key
    ? bulk.ids.filter((id) => !already.has(id))
    : [];
  /**
   * 체크칸의 세 상태 — **고르지 않은 것을 골랐다고 말하지 않게** 한다.
   *   켜짐 : [전체 선택]으로 고른 묶음이 통째로 골라져 있다
   *   반쯤 : 묶음의 일부만, 또는 지금 쪽에 손으로 고른 문제가 섞여 있다
   *   꺼짐 : 그 밖
   */
  const bulkOn = bulkIds.length > 0 && bulkIds.every((id) => pickedKeys.has(id));
  const bulkPartial = !bulkOn && (
    bulkIds.some((id) => pickedKeys.has(id))
    || results.items.some((it) => pickedKeys.has(String(it.problem_id)))
  );
  // React 는 indeterminate 를 속성으로 못 받는다 — DOM 에 직접 건다(AssignPanel·RenumberModal 과 같다).
  // 그릴 때마다 다시 건다 — 반쯤인 체크를 누르는 순간 브라우저가 스스로 지우고, 상태는 그대로라 한 번만 걸면 거짓이 남는다
  const bulkCheckRef = useRef(null);
  useEffect(() => { if (bulkCheckRef.current) bulkCheckRef.current.indeterminate = bulkPartial; });
  // 결과가 상한을 넘는가 — 글은 하나로 두고, 마우스를 올렸을 때의 설명(title)만 갈린다
  const pickTopOver = results.totalItems > PICK_TOP_LIMIT;
  // 한 쪽뿐이면 결과 전부가 손에 있다 — 그중 고를 것이 하나도 없으면(전부 이미 담김) 누를 거리가 없다
  const pickTopEmpty = results.totalPages <= 1 && results.items.every((it) => already.has(String(it.problem_id)));
  // 고르기를 막는 때 — 쪽 넘기기가 막히는 때(읽는 중·조건 바뀜·식 오류)와 같다. 그때 화면의 결과는 지금 조건의 결과가 아니다.
  // 켜진 체크를 끄는 것은 막지 않는다 — 빼는 것은 화면에 보이는 그 묶음에 대한 일이라 언제 해도 참이다
  const pickTopBlocked = !bulkOn && (searching || staleSearch || !searchAllowed || bulkLoading || pickTopEmpty);

  /** 받은 목록의 위에서부터 고른다 — 이미 담긴 것은 빼고, 이미 고른 것은 두 번 넣지 않는다. 순서는 검색 순서 그대로 뒤에 붙는다 */
  const pickTopFrom = (items, key) => {
    const inPaper = new Set(rowsRef.current.map((r) => String(r.problem_id))); // 받는 사이 담은 것까지 본다
    const ids = items.slice(0, PICK_TOP_LIMIT).map((it) => it.problem_id).filter((id) => id != null && !inPaper.has(String(id)));
    if (ids.length === 0) {
      setToast({ type: 'info', text: `고를 문제가 없습니다 — 위 ${Math.min(items.length, PICK_TOP_LIMIT)}개가 모두 이미 담겨 있습니다.` });
      return;
    }
    setPicked((p) => {
      const has = new Set(p.map(String));
      return [...p, ...ids.filter((id) => !has.has(String(id)))];
    });
    setBulk({ key, ids: ids.map(String) });
  };

  const togglePickTop = async () => {
    if (bulkBusyRef.current || replacing) return;
    // 이미 통째로 골라져 있다 → **그 묶음만** 뺀다(다른 조건으로 골라 둔 문제는 그대로)
    if (bulkOn) {
      const ids = new Set(bulkIds);
      setPicked((p) => p.filter((x) => !ids.has(String(x))));
      setBulk(null);
      return;
    }
    if (pickTopBlocked) return;
    const snap = successfulSearchRef.current; // 지금 화면의 결과를 만든 검색
    if (snap == null) return;
    // 한 쪽뿐이면 결과가 이미 손에 있다 — 서버를 다시 부르지 않는다
    if (results.totalPages <= 1) { pickTopFrom(results.items, snap.key); return; }

    const seq = searchSeq.current; // 그사이 검색을 다시 하거나 조건을 고치면(seq 증가) 받은 것을 버린다
    bulkBusyRef.current = true;
    setBulkLoading(true);
    try {
      // 1쪽을 상한 크기로 한 번 — 서버의 검색 순서 그대로 「위에서 100개」다. 카드를 그리지 않으니 본문은 뺀다
      const d = await searchComposePage({
        expr: snap.expr, form: snap.form, page: 1, perPage: PICK_TOP_LIMIT, withBody: false,
      }, getAccessToken);
      if (!aliveRef.current || seq !== searchSeq.current || successfulSearchRef.current?.key !== snap.key) return;
      // 담을 때 미리보기·난이도·라벨·개념을 들고 가는 자리 — 넘겨 보지 않은 쪽의 문제도 담기에 필요하다.
      // 이미 본 것은 덮지 않는다(카드가 받은 것은 본문까지 실린 한 벌이다)
      d.items.forEach((it) => {
        const k = String(it.problem_id);
        if (!seenRef.current.has(k)) seenRef.current.set(k, it);
      });
      pickTopFrom(d.items, snap.key);
      // 청한 만큼 안 왔으면 **고른 수를 말한다** — 체크칸 글(「최대 100문제」)을 믿고 덜 고른 채 담게 두지 않는다
      const expected = Math.min(d.totalItems ?? results.totalItems, PICK_TOP_LIMIT);
      if (d.items.length < expected) {
        setToast({ type: 'info', text: `서버가 ${d.items.length}개만 돌려줘 위에서 ${d.items.length}개까지만 골랐습니다.` });
      }
    } catch (e) {
      if (!aliveRef.current || seq !== searchSeq.current) return;
      setToast({ type: 'error', text: `전체 선택을 하지 못했습니다 — ${e.message || '문제를 불러오지 못했습니다.'}` });
    } finally {
      bulkBusyRef.current = false;
      if (aliveRef.current) setBulkLoading(false);
    }
  };

  const drag = usePaperListDrag({
    rows,
    enabled: isOpen && busy == null && actionMode == null && !previewOpen
      && !confirmClose && confirmAssign == null && conceptDetail == null,
    onMove: (id, boundary) => {
      if (!canEditNow()) return;
      const current = rowsRef.current;
      const from = uniqueRowIndex(current, id);
      if (from < 0) return;
      const next = movePaperRowAtBoundary(current, id, boundary);
      if (commitRows(next)) {
        selectAfterChange(id, true);
        setToast({ type: 'info', text: `${from + 1}번을 ${uniqueRowIndex(next, id) + 1}번으로 이동했습니다.` });
      }
    },
    onInsert: (id, boundary) => insertProblems([id], boundary),
  });
  const { dropAt, source: dragSource, end: endDrag } = drag;

  const selectProblem = (id, fromPaper = false) => {
    if (!canEditNow() || dragSource || uniqueRowIndex(rowsRef.current, id) < 0) return;
    if (String(selectedId) === String(id)) { resetSelection(); return; }
    cancelEditing();
    setSelectedId(id, fromPaper);
    revealRowRef.current = id;
    if (fromPaper && !paperAsColumn) setPaperOpen(false);
  };
  const startAction = (mode) => {
    if (!canEditNow() || uniqueRowIndex(rowsRef.current, selectedId) < 0) return;
    endDrag();
    cancelEditing();
    actionRowsRef.current = rowsRef.current;
    setActionMode(mode);
  };
  const finishAction = () => {
    cancelEditing();
    focusRowRef.current = { id: selectedId };
  };
  const commitNumber = (number) => {
    if (!canEditNow() || actionRowsRef.current !== rowsRef.current) return;
    const current = rowsRef.current;
    const from = uniqueRowIndex(current, selectedId);
    if (from < 0 || !Number.isInteger(number) || number < 1 || number > current.length) return;
    const next = actionMode === 'move' ? movePaperRow(current, selectedId, number - 1)
      : actionMode === 'swap' ? swapPaperRows(current, selectedId, number - 1) : current;
    if (commitRows(next)) {
      selectAfterChange(selectedId, true);
      setToast({ type: 'info', text: actionMode === 'move'
        ? `${from + 1}번을 ${number}번으로 이동했습니다.` : `${from + 1}번과 ${number}번의 순서를 교체했습니다.` });
    } else finishAction();
  };

  // 위 레이어가 Esc를 소유한다. 이 상태에서는 부모 Modal의 closeOnEsc도 함께 끈다.
  useEffect(() => {
    if (!isOpen || previewOpen || confirmClose || confirmAssign != null || conceptDetail != null) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape' || busy != null) return;
      if (dragSource) { endDrag(); return; }
      if (actionMode != null) { cancelEditing(); focusRowRef.current = { id: selectedId }; }
      else if (selectedId != null) resetSelection();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, previewOpen, confirmClose, confirmAssign, conceptDetail, busy, dragSource, endDrag,
    actionMode, selectedId, cancelEditing, resetSelection]);

  useEffect(() => {
    if (selectedId != null && uniqueRowIndex(rows, selectedId) < 0) resetSelection();
  }, [rows, selectedId, resetSelection]);

  // 그린 지면의 ID를 읽는다. 배치가 늦을 때 옛 번호를 새 배열의 인덱스로 해석하지 않는다.
  const onPaperClick = (event) => {
    if (event.target.closest?.('button, a, input, select, textarea, label')) return;
    if (window.getSelection?.()?.isCollapsed === false) return;
    const hit = event.target.closest?.('.pv4-problem-item[data-problem-id]');
    if (!hit || !event.currentTarget.contains(hit)) return;
    const id = hit.dataset.problemId;
    const rendered = laid?.problems?.[Number(hit.dataset.problemNumber) - 1];
    if (String(rendered?.problem_id) !== id) return;
    selectProblem(id, true);
  };

  // 선택 강조는 모달 안의 지면에만 둔다. 인쇄 크기를 바꾸는 border/padding은 쓰지 않는다.
  useLayoutEffect(() => {
    const box = previewBoxRef.current;
    if (!isOpen || previewOpen || selectedIdx < 0 || !box) return undefined;
    const el = Array.from(box.querySelectorAll('.pv4-problem-item[data-problem-id]'))
      .find((node) => node.dataset.problemId === String(selectedId));
    if (!el) return undefined;
    const classes = ['outline', 'outline-2', 'outline-offset-2', 'outline-primary'];
    el.classList.add(...classes);
    if (!selectedFromPaper && (paperAsColumn || paperOpen)) {
      // 목록에서 고르면 지면 가운데로 안내한다. 지면을 직접 누르면 읽던 위치를 유지한다.
      // 배율이 늦게 맞춰져도 실제 렌더링 크기로 다시 계산한다.
      scrollBoxTo(box, centerTarget(box, el), 'auto');
    }
    return () => el.classList.remove(...classes);
  }, [isOpen, previewOpen, selectedIdx, selectedId, selectedFromPaper, problemPages, previewBoxRef, paperAsColumn, paperOpen, paperScale]);

  useLayoutEffect(() => {
    const box = drag.listRef.current;
    if (!box || dragSource) return;
    const findRow = (id) => Array.from(box.querySelectorAll('[data-paper-row]'))
      .find((node) => node.dataset.paperRow === String(id));
    if (revealRowRef.current != null) {
      const row = findRow(revealRowRef.current);
      if (row) {
        const outer = box.getBoundingClientRect();
        const inner = row.getBoundingClientRect();
        if (inner.top < outer.top || inner.bottom > outer.bottom) {
          box.scrollTop += inner.top < outer.top || inner.height > box.clientHeight
            ? inner.top - outer.top : inner.bottom - outer.bottom;
        }
      }
      revealRowRef.current = null;
    }
    if (focusRowRef.current != null) {
      const target = findRow(focusRowRef.current.id)?.querySelector('[data-row-select]') || listHeadingRef.current;
      target?.focus({ preventScroll: true });
      focusRowRef.current = null;
    }
  });

  const putInSlot = (id) => {
    if (!canEditNow() || !replacing || id == null || actionRowsRef.current !== rowsRef.current) return;
    const current = rowsRef.current;
    const idx = uniqueRowIndex(current, selectedId);
    if (idx < 0) return;
    if (current.some((row) => String(row.problem_id) === String(id))) {
      setToast({ type: 'info', text: '이미 담긴 문제입니다. 두 자리만 바꾸려면 순서 교체를 사용하세요.' });
      return;
    }
    const it = seenRef.current.get(String(id));
    if (!it) return;
    const next = current.slice();
    next[idx] = {
      problem_id: id,
      points: current[idx].points,
      preview: it.preview ?? '',
      difficulty: it.difficulty ?? null,
      labels: labelText(it.labels),
      isDeleted: isDeletedItem(it),
      concepts: conceptPairsOf(it),
    };
    if (!commitRows(next)) return;
    selectAfterChange(id, true);
    setPicked((p) => p.filter((x) => String(x) !== String(id)));
    setToast({ type: 'info', text: `${idx + 1}번 문항을 #${id} 로 바꿨습니다 — 저장해야 반영됩니다.` });
  };

  const runSearch = () => load(form, 1);
  /**
   * 초기화 — 칸만 비우는 게 아니라 **목록까지 되돌린다.** 조건은 비었는데 아래 목록이 옛 조건
   * 그대로면 화면이 자기 상태를 거짓으로 말한다. 창을 열 때 하는 일(load(EMPTY_FORM, 1))과 같다.
   * 검색 중이어도 잠그지 않는다 — 오래 걸리는 검색에서 빠져나오는 길이 이것뿐이고,
   * 늦게 도착하는 옛 응답은 searchSeq 가 이미 버린다.
   */
  const resetSearch = () => {
    // 강의 줄도 푼다 — 조건을 비운 화면이 「강의 그대로」라고 말하면 거짓이다
    sylExprRef.current = null;
    setSylQuery(null);
    setSylPick(null);
    formRef.current = EMPTY_FORM;
    setForm(EMPTY_FORM);
    load(EMPTY_FORM, 1);
  };
  /** 강의 줄 고르기 — 곧바로 찾는다. 폼은 감춰질 뿐 값은 그대로다([강의 해제]하면 다시 쓰인다). 고른 문제(picked)는 손대지 않는다(판 2 §4-4).
      강의 칸은 닫지 않는다 — 붙박이 칸이라 다른 줄을 이어서 고른다(오너 2026-09-14) */
  const applySyllabusPick = (pick) => {
    const draft = syllabusDrafts.current.get(`${pick.syllabusId}:${pick.nodeId}`);
    changeSyllabusQuery(draft === undefined ? pick.query : draft, pick);
    setSylPick({ ...pick, query: cloneQuery(pick.query) });
    load(form, 1);
  };
  const clearSyllabusPick = () => {
    sylExprRef.current = null;
    setSylQuery(null);
    setSylPick(null);
    load(form, 1);
  };

  // 뒤늦게 온 판을 어떻게 할지는 사용자가 정한다. 어느 쪽을 골라도 문항 목록을 다시 읽은 것은
  // 사실이므로 '문항을 못 읽었다' 표시는 함께 내린다 — 저장에서 items 를 빼는 안전장치도 같이 풀린다.
  const lateRows = lateItems == null ? [] : toRows(pickItems(lateItems));
  const reseedFromServer = () => {
    seed(lateItems);
    setLateItems(null);
    setToast({ type: 'info', text: '서버 값으로 다시 읽었습니다.' });
  };
  const keepMine = () => {
    // 화면에 담긴 문항이 곧 저장될 값이다 — 기준선은 서버 문항으로 잡아야 무엇이 바뀌는지가 맞는다
    seedRef.current = { ...seedRef.current, rows: lateRows };
    setItemsUnknown(false);
    setToast({ type: 'info', text: `저장하면 지금 담긴 ${rows.length}문항이 서버 문항 ${lateRows.length}개를 대신합니다.` });
    setLateItems(null);
  };

  // 닫기·백드롭·ESC 가 모두 이리로 온다. 확인을 받아도 dirty 는 내리지 않는다 —
  // 사용자가 [계속 편집]으로 돌아왔을 때 경고가 사라져 있으면 안 된다.
  const requestClose = () => {
    if (busy) return;
    if (dirty) {
      // ESC 를 두 번 누르는 것으로는 못 닫는다 — 확인 모달의 [그래도 닫기]를 눌러야 버려진다
      setConfirmClose(true);
      return;
    }
    onClose?.();
  };

  /** 확인창은 쓰기 전에 떠야 하므로, 등록과 같은 검증을 먼저 통과시킨다. */
  const validateForAssign = () => {
    const nextTitle = String(title ?? '').trim();
    if (nextTitle === '') {
      setToast({ type: 'error', text: '제목을 입력해 주세요.' });
      return null;
    }
    if (rows.length === 0 && !itemsUnknown) {
      setToast({ type: 'error', text: '문항을 최소 하나는 담아 주세요.' });
      return null;
    }
    const dup = duplicateIds(toPayload(rows));
    if (dup.length > 0) {
      setToast({
        type: 'error',
        text: `같은 문제가 두 번 담겨 있습니다(${dup.map((v) => `#${v}`).join(', ')}) — 한쪽을 빼고 다시 저장하세요.`,
      });
      return null;
    }
    return { title: nextTitle, count: rows.length };
  };

  const submit = async (thenAssign) => {
    if (busy || submitRef.current) return;
    // 고칠 게 없는데 배정하러 가는 길 — 빈 PATCH 로 revision 을 괜히 올리지 않는다
    if (thenAssign && effectiveId != null && !dirty) {
      submitRef.current = true;
      setConfirmAssign(null);
      onAssignRequested?.(effectiveId, {
        savedNow: false,
        title: String(title ?? '').trim(),
        count: rows.length,
      });
      // 기존 소비처는 곧바로 창을 닫지만, 같은 렌더의 연속 클릭까지는 이 부품 자체가 막는다.
      Promise.resolve().then(() => { submitRef.current = false; });
      return;
    }
    // title 이 문자열이 아니면(null 포함) 서버가 400 이다(계약 §1-3). 상태는 늘 문자열로 심기지만
    // (seed 의 `p?.title ?? ''`), 상세가 숫자·null 을 주면 그 값이 그대로 앉는다 — 나가기 직전에 굳힌다.
    const t = String(title ?? '').trim();
    const desc = String(description ?? '').trim(); // 같은 이유로 설명도 굳힌다(비문자열이면 .trim() 이 터진다)
    if (t === '') {
      setToast({ type: 'error', text: '제목을 입력해 주세요.' });
      return;
    }
    if (rows.length === 0 && !itemsUnknown) {
      setToast({ type: 'error', text: '문항을 최소 하나는 담아 주세요.' });
      return;
    }
    const items = toPayload(rows);
    // 계약 §1-3 — 같은 problem_id 두 번이면 400. 서버 오류를 보기 전에 여기서 막고 어느 번호인지 짚는다
    const dup = duplicateIds(items);
    if (dup.length > 0) {
      setToast({
        type: 'error',
        text: `같은 문제가 두 번 담겨 있습니다(${dup.map((v) => `#${v}`).join(', ')}) — 한쪽을 빼고 다시 저장하세요.`,
      });
      return;
    }
    submitRef.current = true;
    setBusy(thenAssign ? 'assign' : 'save');
    setToast(null);
    setConfirmClose(false); // 저장하고 나면 "사라집니다" 경고가 남아 있으면 안 된다
    try {
      if (isNew) {
        const d = await createPaper(
          { title: t, description: desc, items },
          getAccessToken,
        );
        if (d === AUTH_REDIRECT) return; // 401 → /signin 이동 중. 성공이라고 말하지 않는다
        // 학생을 바꾸며 창이 닫힌 뒤 도착한 저장 응답은 서버 저장까지만 사실이다.
        // 이전 학생의 onAssignRequested를 부르면 새 화면에서 옛 학생 배정이 시작된다.
        if (!aliveRef.current) return;
        const newId = d?.paper_id ?? d?.id;
        seedRef.current = { title: t, description: desc, rows };
        if (newId == null) {
          // 만들어지긴 했는데 어디로 가야 할지 모른다 — 말없이 닫으면 만들어졌는지조차 알 수 없다
          setToast({ type: 'error', text: '시험지는 만들어졌지만 번호를 받지 못했습니다 — 목록에서 확인해 주세요.' });
          return;
        }
        promotePrintDraft(userId, printContext, newId);
        setCreatedId(newId);
        setToast({ type: 'info', text: '시험지를 만들었습니다.' });
        onSaved?.(newId, { thenAssign });
        if (thenAssign) onAssignRequested?.(newId, {
          savedNow: true,
          title: t,
          count: items.length,
        });
      } else {
        // 보낸 키만 반영된다 — 문항을 못 읽은 상태면 items 를 빼서 서버 문항을 지키고
        // 제목·설명만 고친다.
        const patch = { title: t, description: desc };
        if (!itemsUnknown) patch.items = items;
        const d = await updatePaper(effectiveId, patch, getAccessToken);
        if (d === AUTH_REDIRECT) return;
        if (!aliveRef.current) return;
        seedRef.current = { title: t, description: desc, rows };
        setToast({ type: 'info', text: '저장했습니다 — 새 판(revision)이 쌓였습니다.' });
        onSaved?.(effectiveId, { thenAssign });
        if (thenAssign) onAssignRequested?.(effectiveId, {
          savedNow: true,
          title: t,
          count: items.length,
        });
      }
    } catch (e) {
      // 사유(409/423)를 아는 것은 우리 문구로, 모르는 것(중복 400 등)은 서버 문구로 흘린다
      setToast({ type: 'error', text: describeApiError(e, '저장하지 못했습니다.') });
    } finally {
      submitRef.current = false;
      if (thenAssign && aliveRef.current) setConfirmAssign(null);
      if (aliveRef.current) setBusy(null);
    }
  };

  const requestAssign = () => {
    if (busy) return;
    // 독립 시험지 화면은 기존 행동을 바꾸지 않고, 학생 문맥에서만 공용 확인을 둔다.
    if (!assignmentTargetName) {
      submit(true);
      return;
    }
    const checked = validateForAssign();
    if (checked == null) return;
    setToast(null);
    setConfirmAssign(checked);
  };

  /* 이 창이 든 미저장 편집은 **페이지가 모른다** — 페이지의 가드(ExamPapers.confirmDiscard)는
     문항 편집·채점·검수 세 패널이 올려 준 것만 본다. 그래서 창이 자기 것을 스스로 덮어씌운다.
     ref 로 읽는 이유는 참조를 고정하기 위해서다 — 매 렌더마다 새 함수면 이 창 안의 문제 번호
     링크 스무 개가 함께 다시 그려진다. */
  dirtyRef.current = dirty;

  if (!isOpen) return null;

  /* 앞의 두 열 — 어떤 폭에서도 가로로 나란히 선다.
     지면이 서랍일 때는 flex-1 둘이 남는 자리를 반씩 갖고, 지면이 열로 설 때만(paperAsColumn) 32/30% 로 못 박는다
     — 그 32/30% 는 강의 칸을 뺀 폭의 몫이다(composeColumnWidth · 강의 칸을 접으면 종전 w-[32%]·w-[30%] 와 같다).
     고정 폭 열은 flex-none 으로 basis 0 을 걷어야 한다 — 안 그러면 폭이 무시되고 접힌다. */
  const COL = 'flex min-h-0 min-w-0 flex-1 flex-col';

  return (
    <ProblemNavGuard confirm={confirmLeave}>
      <Modal
        isOpen={isOpen}
        onClose={requestClose}
        title={isNew ? '새 시험지 만들기' : '시험지 편집'}
        size="max"
        /* max 의 1400px 상한을 걷는다(오너 2026-09-15 「가로폭을 유동적으로」) — 화면 폭의 98% 를 그대로 쓴다.
           화면 1428 이하는 종전과 같은 폭이다. 열/서랍 판정이 화면 폭이 아니라 열 상자 폭을 재는 이유가 이것이다 */
        className="!max-w-none"
        maxHeight="92vh"
        fixedHeight
        preventScroll
        inactive={confirmClose || confirmAssign != null || conceptDetail != null}
        /* ESC 의 임자는 위에서부터다: 전체화면 미리보기 > 교체 모드 > 닫기 확인 > 모달 닫기.
           앞의 셋이 떠 있는 동안 모달까지 ESC 를 먹으면 확인 모달이 닫히자마자 다시 열린다.
           강의 칸은 붙박이라(2026-09-14) ESC 를 먹지 않는다 — 늘 펼쳐져 있어 여기 걸면 ESC 로 창을 못 닫는다. */
        closeOnEsc={!previewOpen && selectedId == null && actionMode == null && dragSource == null && !confirmClose && confirmAssign == null && conceptDetail == null}
        /* 미리보기(9999980)·인쇄 오버레이(9999990)가 이 모달 위에 뜨게 한 칸 낮춘다 */
        zIndex={9999970}
        bodyClassName="flex min-h-0 flex-1 flex-col p-0"
        headerActions={
          <span className="mr-2 text-xs text-gray-400">
            {rows.length}문항
          </span>
        }
        footer={
          <div className="flex w-full min-w-0 flex-col gap-1.5">
            <div className="flex w-full flex-wrap items-center gap-2">
              <div className="min-w-0 flex-1">
                <p
                  role={toast?.type === 'error' ? 'alert' : 'status'}
                  aria-live={toast?.type === 'error' ? 'assertive' : 'polite'}
                  className={`text-xs ${toast ? (toast.type === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
                >
                  {toast?.text || ''}
                </p>
                {dirty && (
                  <p className="text-[11px] text-amber-600">
                    저장하지 않은 변경이 있습니다{isNew ? ' — [시험지 만들기]를 눌러야 서버에 생깁니다.' : '.'}
                  </p>
                )}
              </div>
              <div className="ml-auto flex shrink-0 gap-2">
                <Button size="sm" variant="secondary" onClick={requestClose} disabled={busy != null} className="min-h-[44px] md:min-h-0">{cancelLabel}</Button>
                {assignPrimary ? (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => submit(false)} disabled={busy != null || lateItems != null || (!isNew && !dirty)} className="min-h-[44px] md:min-h-0">
                      <Save className="mr-1 inline size-3.5" />
                      {busy === 'save' ? '저장 중…' : '시험지만 저장'}
                    </Button>
                    <Button size="sm" onClick={requestAssign} disabled={busy != null || lateItems != null} className="min-h-[44px] md:min-h-0">
                      {busy === 'assign' ? '저장 중…' : assignLabel}
                    </Button>
                  </>
                ) : (
                  <>
                    {/* 독립 시험지 화면의 순서·variant·문구는 기존 그대로다. */}
                    <Button size="sm" variant="secondary" onClick={requestAssign} disabled={busy != null || lateItems != null} className="min-h-[44px] md:min-h-0">
                      {busy === 'assign' ? '저장 중…' : assignLabel}
                    </Button>
                    <Button size="sm" onClick={() => submit(false)} disabled={busy != null || lateItems != null || (!isNew && !dirty)} className="min-h-[44px] md:min-h-0">
                      <Save className="mr-1 inline size-3.5" />
                      {busy === 'save' ? '저장 중…' : isNew ? '시험지 만들기' : '저장'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        }
      >
        {/* 제목·설명 — 세 열이 모두 이 값을 쓴다(우측 지면의 머리글도 여기서 온다) */}
        <div className="shrink-0 space-y-2 border-b bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap gap-2">
            <label className="min-w-0 flex-1">
              <span className="sr-only">제목</span>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={busy != null}
                maxLength={100}
                placeholder="시험지 제목 (필수) — 예: 2학기 중간 대비 이차함수"
                className="w-full rounded-xl border px-3 py-2 text-sm focus:border-primary focus:outline-none disabled:bg-gray-100"
              />
            </label>
            <label className="min-w-0 flex-1">
              <span className="sr-only">설명(선택)</span>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={busy != null}
                maxLength={500}
                placeholder="설명 (선택) — 목적·범위 메모"
                className="w-full rounded-xl border px-3 py-2 text-sm focus:border-primary focus:outline-none disabled:bg-gray-100"
              />
            </label>
          </div>
          {itemsUnknown && lateItems == null && (
            <p role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
              서버가 준 문항 목록을 읽지 못했습니다 — 가운데가 비어 보여도 시험지에는 문항이 남아 있습니다.
              안전을 위해 저장할 때 문항은 건드리지 않고 제목·설명만 보냅니다.
              {rows.length > 0 && (
                <b className="ml-1">지금 담은 {rows.length}문항도 저장에 포함되지 않습니다.</b>
              )}
              {repairing && <span className="ml-1 text-amber-600">다시 읽는 중…</span>}
              {!repairing && (
                <button type="button" onClick={repair} className="ml-1 underline hover:text-amber-900">
                  다시 읽기
                </button>
              )}
              {repairError && <span className="ml-1 text-danger">{repairError}</span>}
            </p>
          )}

          {/* 뒤늦게 도착한 서버 문항 — 사용자가 담아 둔 것을 말없이 덮지 않는다.
              닫기·백드롭·ESC 를 확인으로 막는 이 모달에서 편집을 덮는 이 길만 안 묻는 것이 모순이다. */}
          {lateItems != null && (
            <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-2.5 text-xs leading-relaxed text-amber-900">
              <p>
                서버에서 이 시험지의 문항 <b>{lateRows.length}개</b>를 뒤늦게 읽었습니다.
                지금 화면에는 직접 담은 <b>{rows.length}문항</b>이 있습니다 — 어느 쪽을 쓸지 골라 주세요.
              </p>
              <p className="mt-1">
                [내 편집 유지]는 화면 그대로 두고, 저장할 때 지금 담긴 문항이 서버 문항을 대신합니다.
                [서버 값으로 다시 읽기]는 제목·설명·문항을 모두 서버 값으로 되돌립니다 — 지금 편집은 사라집니다.
                고르기 전까지는 저장하지 않습니다.
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <Button size="xs" onClick={keepMine}>내 편집 유지</Button>
                <Button size="xs" variant="secondary" onClick={reseedFromServer}>서버 값으로 다시 읽기</Button>
              </div>
            </div>
          )}

          {/* 교체 모드 안내 — 세 열 어디를 보고 있어도 읽히도록 열 위에 둔다.
              라이브 리전은 늘 붙여 두고 비었을 때 감춘다(모드에 들어갈 때 낭독되게). */}
          <div
            role="status"
            aria-live="polite"
            className={replacing
              ? 'flex flex-wrap items-center gap-1.5 rounded-xl border border-primary/30 bg-primary/5 px-2.5 py-1.5 text-xs text-gray-700'
              : 'sr-only'}
          >
            {replacing && (
              <>
                <Replace aria-hidden="true" className="size-3.5 text-primary" />
                <span>
                  <b>{selectedIdx + 1}번 문항</b>을 바꿉니다 — 왼쪽 [검색]에서 새 문제를 고르세요.
                  후보를 확인한 뒤 <b>이 문제로 교체</b>를 누르세요.
                </span>
                <Button size="xs" variant="secondary" onClick={cancelEditing}>교체 취소</Button>
                <span className="text-gray-400">ESC 로도 취소됩니다</span>
              </>
            )}
          </div>
        </div>

        {/* 열이 서는 자리.
            · relative — 서랍(absolute)의 기준점이다.
            · overflow-hidden — 닫힌 서랍은 이 상자 오른쪽 **밖**에 서 있다. 안 자르면 그만큼
              가로가 넘쳐 모달에 없던 스크롤바가 생긴다.
            · pr-9(36px) — 손잡이가 앉을 띠를 지면이 서랍일 때만 비워 둔다. 이 자리를 만들지 않으면
              손잡이가 담은 문제 열의 오른쪽 버튼(위·아래·빼기) 위에 겹쳐 그 행을 못 누른다.
              서랍 자신은 이 여백을 무시하고 끝까지 붙는다 — absolute 의 기준은 padding box 라
              right-0 이 안쪽 여백이 아니라 테두리 안쪽 끝을 가리킨다.
            · 서랍인지 열인지는 paperAsColumn 이 정한다 — 강의 칸을 뺀 이 상자의 폭이 1222(종전 xl 경계) 미만이면 서랍(2026-09-15).
            · ref — 이 상자의 겉 폭을 잰다. 겉 폭이라 위 pr-9 가 붙었다 떨어져도 판정이 흔들리지 않는다. */}
        <div ref={rowRef} className={`relative flex min-h-0 flex-1 overflow-hidden ${paperAsColumn ? '' : 'pr-9'}`}>
          {/* 강의 칸 손잡이 — 칸을 접고 편다. 칸은 창을 열면 펼쳐져 있다(오너 2026-09-14 「기본이 펼쳐지는 형태」).
              고른 줄의 조건은 같은 검색 열에서 조절한다(W07). 손잡이는 기존 지면 서랍과 같은 세로쓰기·폭을 유지한다. */}
          <button
            type="button"
            onClick={() => setSylOpen((v) => !v)}
            aria-expanded={sylOpen}
            aria-controls="compose-syllabus-panel"
            aria-label={sylOpen ? '강의 접기' : '강의 펼치기'}
            title={sylOpen ? '강의 칸을 접습니다' : '강의(진도 순서로 짠 문제 찾는 조건) 칸을 폅니다 — 한 줄을 골라 그 조건으로 찾습니다'}
            className={`flex w-7 shrink-0 flex-col items-center gap-1 border-r py-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${
              sylOpen || sylPick ? 'bg-primary/10 text-primary' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
            }`}
          >
            <BookMarked aria-hidden="true" className="size-3.5" />
            <span className="text-[12px] font-bold leading-none [writing-mode:vertical-rl]">강의</span>
            {sylOpen
              ? <ChevronLeft aria-hidden="true" className="size-3.5" />
              : <ChevronRight aria-hidden="true" className="size-3.5" />}
          </button>

          {/* 강의 칸 — 붙박이(320px). 접어도 내리지 않는다(hidden) — 다시 펴면 고르던 강의·펼친 줄이 그대로다 */}
          <SyllabusPickOverlay id="compose-syllabus-panel" hidden={!sylOpen} onPick={applySyllabusPick} activeSyllabusId={sylPick?.syllabusId ?? null} activeNodeId={sylPick?.nodeId ?? null} />

          {/* ── 좌: 검색 ───────────────────────────────────────────────── */}
          <section
            className={`${COL} relative border-r ${paperAsColumn ? 'flex-none' : ''}`}
            style={paperAsColumn ? { width: composeColumnWidth(COMPOSE_SEARCH_SHARE, sylOpen) } : undefined}
            aria-label="문제 검색"
          >
            {/* 검색 조건.
                · **오른쪽 기둥(초기화·검색)이 위 3행을 통째로 걸친다**(오너 지시 2026-08-29) —
                  그래서 조건 3행을 한 묶음으로 싸고 기둥을 그 옆에 세운다. flex 의 기본 늘이기
                  (align-items:stretch)가 기둥 높이를 3행에 맞추고, 두 버튼이 flex-1 로 반씩 갖는다.
                  기둥에 폭을 못박지 않는다 — 늘이기가 두 버튼을 「초기화」(더 긴 쪽) 폭에 맞춘다.
                · 난이도는 여기서 아래 한 행으로 내려갔다. 그 행은 기둥 밑을 지나 끝까지 간다.
                · relative z-10 — 개념 후보 목록이 이 머리 안에서 아래로 뜨는데, 결과 목록은
                  다음 형제라 나중에 그려진다. 층을 안 주면 후보가 카드 뒤로 들어간다. */}
            <div className={`relative z-10 shrink-0 space-y-2 border-b bg-gray-50 px-3 py-2.5 ${sylPick ? 'max-h-[60%] overflow-y-auto' : ''}`}>
              {sylPick && (
                <SyllabusComposeConditions
                  key={`${sylPick.syllabusId}:${sylPick.nodeId}`}
                  pick={sylPick}
                  query={sylQuery}
                  onChange={changeSyllabusQuery}
                  onApplySearch={(q) => { changeSyllabusQuery(q); load(form, 1); }}
                  onRevert={() => changeSyllabusQuery(sylPick.query)}
                  onChoose={() => setSylOpen(true)}
                  onClear={clearSyllabusPick}
                  dict={conceptDict}
                  labelNames={labelNames}
                  onRememberLabels={rememberLabels}
                  searching={searching}
                />
              )}
              {/* 강의를 고른 동안 폼(개념·라벨·번호·작성자·난이도·정답·풀이)은 감추고 검색에도 싣지 않는다 — 조건은 위 강의 조건 카드 하나다.
                  「결과 좁히기」 칸(열기 버튼·안내·필터 칩)은 자리만 차지해 걷었다(오너 2026-09-14). 폼 값은 [강의 해제]하면 다시 쓰인다 */}
              <div hidden={!!sylPick}>
              {/* 이 묶음(조건 3행 + 버튼 기둥)이 **아래 ④행보다 위 층**에 선다.
                  개념 후보 목록은 ①행 안에서 층을 갖는데, 그 층은 이 묶음 **안에서만** 통한다 —
                  ④행은 묶음 밖 형제라 거기까지 닿지 않아 후보가 난이도·정답·풀이 줄 뒤로 들어갔다
                  (오너 지적 2026-08-29). 묶음째 올리면 후보가 그 줄 위로 나온다.
                  후보가 닫혀 있을 때도 층을 유지하는 것은 둘이 세로로 나란해 겹칠 일이 없어서다 —
                  여닫힘을 부품 밖으로 끌어내는 배선보다 이편이 단순하다. */}
              <div className="relative z-20 flex gap-1.5">
                <div className="min-w-0 flex-1 space-y-2">
                  {/* ① 개념 — 글자를 치면 후보가 뜨고, 고른 것은 아래 칩 줄에 쌓인다.
                      OR/AND 는 **입력 상자 안쪽**에 선다(오너 지시 2026-08-29) — 조건과 그
                      조건을 묶는 방식이 한 상자에 있어야 딸린 것으로 읽힌다.
                      AND(모두 포함)는 2026-09-13 에 풀렸다 — 주소줄의 concept_ids 는 여전히 「하나라도」라
                      AND 는 본문 검색의 식으로 나간다(load). 문제(/problem-hub) 검색과 **같은 부품**이다.
                      강의 조건을 고른 동안의 잠금(오너 결정 D3)은 걷었다(2026-09-14) — 그동안 이 폼은 통째로 감춰지고
                      검색에도 안 실려서, 강의 식에 「개념 모두」가 겹칠 일이 없다. */}
                  <ConceptFilterInput
                    value={form.conceptIds}
                    onChange={(ids) => changeForm({ ...form, conceptIds: ids })}
                    dict={conceptDict}
                    loading={dictLoading}
                    onRetry={() => setDictKey((k) => k + 1)}
                    onSubmit={runSearch}
                    trailing={(
                      <ConceptMatchToggle
                        value={form.conceptMatch}
                        onChange={(m) => changeForm({ ...form, conceptMatch: m })}
                      />
                    )}
                  />
                  <ConceptAllHint match={form.conceptMatch} count={form.conceptIds.length} />
                  {/* ② 라벨 — 난이도가 빠져 한 줄을 다 쓴다 */}
                  <input
                    type="text"
                    value={form.labels}
                    onChange={(e) => changeForm({ ...form, labels: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) runSearch(); }}
                    aria-label="라벨"
                    placeholder="라벨 — 콤마로 여러 개(모두 포함)"
                    className="w-full rounded-lg border px-2 py-1.5 text-xs focus:border-primary focus:outline-none"
                  />
                  {/* ③ 문제 ID · 작성자 */}
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      value={form.problemIds}
                      onChange={(e) => changeForm({ ...form, problemIds: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) runSearch(); }}
                      aria-label="문제 ID"
                      placeholder="문제 ID — 콤마로 여러 개"
                      className="min-w-0 flex-1 rounded-lg border px-2 py-1.5 text-xs focus:border-primary focus:outline-none"
                    />
                    <input
                      type="text"
                      value={form.author}
                      onChange={(e) => changeForm({ ...form, author: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) runSearch(); }}
                      aria-label="작성자"
                      placeholder="작성자"
                      className="min-w-0 flex-1 rounded-lg border px-2 py-1.5 text-xs focus:border-primary focus:outline-none"
                    />
                  </div>
                </div>
                <div className="flex shrink-0 flex-col gap-1.5">
                  <Button size="sm" variant="secondary" className="flex-1" onClick={resetSearch}>초기화</Button>
                  <Button size="sm" className="flex-1" onClick={runSearch} disabled={searching || !searchAllowed}>검색</Button>
                </div>
              </div>
              {/* ④ 난이도·정답·풀이·분석필요를 한 행으로(오너 지시 2026-08-29).
                  시험지에 담을 문항이라 "정답·풀이가 있는가"가 실제로 중요하다(계약 §3의 필터).
                  난이도 옵션에서 「(보통)」 같은 뜻말을 뺀 이유는 폭이다 — select 는 닫혀 있어도
                  가장 긴 옵션에 맞춰 벌어지고, 「3 (보통)」을 두면 이 다섯이 한 행에 못 선다.
                  1~5 는 결과 카드의 난이도 배지와 같은 표기라 화면 안에서 뜻이 유지된다.
                  같은 이유로 체크박스 문구에서 「 보기」를 뗐다 — 실측(2026-08-29 · 빌드 CSS 를
                  그대로 물린 격리 하네스): 화면 1280(검색 열 401px · 안쪽 377px)에서 다섯 항목이
                  난이도 83.5 + 정답 74 + 풀이 74 + 「분석 필요만 보기」 90.9 + 건수 42.7 + 간격 32
                  = **397.1px 로 20.1px 넘쳐** 두 줄이 됐다. 「 보기」를 떼면 370.1px 로 들어간다.
                  1440(424px)·1024 2열(460px)은 원래도 한 줄이었다. */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <label className="flex items-center gap-1 text-[11px] text-gray-500">
                  난이도
                  <select
                    value={form.difficulty}
                    onChange={(e) => changeForm({ ...form, difficulty: e.target.value })}
                    className="rounded border px-1 py-0.5 text-xs focus:border-primary focus:outline-none"
                  >
                    <option value="">전체</option>
                    {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <label className="flex items-center gap-1 text-[11px] text-gray-500">
                  정답
                  <select
                    value={form.hasAnswer}
                    onChange={(e) => changeForm({ ...form, hasAnswer: e.target.value })}
                    className="rounded border px-1 py-0.5 text-xs focus:border-primary focus:outline-none"
                  >
                    <option value="">전체</option>
                    <option value="yes">있음</option>
                    <option value="no">없음</option>
                  </select>
                </label>
                <label className="flex items-center gap-1 text-[11px] text-gray-500">
                  풀이
                  <select
                    value={form.hasSolution}
                    onChange={(e) => changeForm({ ...form, hasSolution: e.target.value })}
                    className="rounded border px-1 py-0.5 text-xs focus:border-primary focus:outline-none"
                  >
                    <option value="">전체</option>
                    <option value="yes">있음</option>
                    <option value="no">없음</option>
                  </select>
                </label>
                {/* 개념·난이도가 비어 있는 문제만(BE-20260825-fe-handover §D) — 정답·풀이만
                    없는 문제는 이 필터에 걸리지 않는다 */}
                <label className="flex cursor-pointer items-center gap-1 text-[11px] text-gray-600">
                  <input
                    type="checkbox"
                    checked={form.needsAnalysis}
                    onChange={(e) => {
                      const next = { ...form, needsAnalysis: e.target.checked };
                      changeForm(next);
                      load(next, 1);
                    }}
                    className="size-3.5 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  분석 필요만
                </label>
                <span className="ml-auto text-[11px] text-gray-400">
                  {searching ? '읽는 중…' : successfulSearch ? `${staleSearch || searchError ? '이전 ' : ''}${results.totalItems}건` : '검색 전'}
                </span>
              </div>
              </div>
            </div>

            {/* 결과 머리 — 스크롤 칸 밖에 둔다(오너 2026-09-14): 목록을 내려도 몇 건·몇 쪽인지가 남는다.
                찾은 조건을 요약하던 둘째 줄은 뺐다 — 조건은 바로 위 조건 칸(강의 조건 카드·입력칸)에 이미 보인다 */}
            {/* 왼쪽 [전체 선택] · 오른쪽 건수(오너 2026-10-09). 알림(role=status)은 오른쪽 글에만 단다 — 체크칸까지
                알림 안에 있으면 누를 때마다 줄 전체를 다시 읽어 준다. */}
            {successfulSearch && (
              <div className="flex shrink-0 items-center gap-2 border-b px-3 py-1.5 text-xs font-medium text-gray-600">
                {/* 교체 모드에서는 카드의 체크칸도 숨으므로 함께 숨는다. 겉모양은 아래 카드의 체크칸과 같은 그림
                    (16px · 둥근 모서리 · 골랐으면 메인 색 채움)이다 — 같은 목록의 같은 조작이다. 진짜 input 은
                    눈에서만 감춘다(sr-only) — 키보드·화면 읽기 프로그램·indeterminate 는 그대로 산다 */}
                {!replacing && results.items.length > 0 && (
                  <label
                    title={bulkOn ? '고른 묶음 빼기'
                      : pickTopEmpty ? '모두 이미 담겨 있습니다'
                        : pickTopOver ? `결과가 ${results.totalItems}건이라 맨 위부터 ${PICK_TOP_LIMIT}개만 고릅니다 — 이미 담긴 문제는 뺍니다`
                          : '검색 결과를 모두 고릅니다 — 이미 담긴 문제는 뺍니다'}
                    className={`flex shrink-0 items-center gap-1.5 ${pickTopBlocked ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer'}`}
                  >
                    <input
                      type="checkbox"
                      ref={bulkCheckRef}
                      checked={bulkOn}
                      onChange={togglePickTop}
                      disabled={pickTopBlocked}
                      className="peer sr-only"
                    />
                    <span
                      aria-hidden="true"
                      className={`flex size-4 shrink-0 items-center justify-center rounded border peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 ${
                        bulkOn || bulkPartial ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white'
                      } ${pickTopBlocked ? 'opacity-50' : ''}`}
                    >
                      {bulkOn ? <Check className="size-3" /> : bulkPartial ? <Minus className="size-3" /> : null}
                    </span>
                    {/* 글은 결과 수와 상관없이 하나다(오너 2026-10-09 「전체 선택 (최대 100문제)」) — 상한을 글이 늘 말한다 */}
                    {`전체 선택 (최대 ${PICK_TOP_LIMIT}문제)`}
                    {bulkLoading && <Loader2 aria-hidden="true" className="size-3 animate-spin text-primary" />}
                  </label>
                )}
                <span role="status" aria-live="polite" className="ml-auto min-w-0 truncate text-right">
                  {staleSearch || searchError || searching ? '이전 검색 결과 · ' : ''}{results.totalItems}건 · {results.page}페이지 · {timeText(successfulSearch.at)}
                </span>
              </div>
            )}
            <div ref={resultListRef} className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
              {staleSearch && <div className="mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700"><span className="min-w-0 flex-1">조건 변경됨 · 아래는 이전 검색 결과입니다. 담기 전에 검색 기준을 확인하세요.</span><Button size="xs" variant="secondary" onClick={runSearch} disabled={searching || !searchAllowed}>다시 검색</Button></div>}
              {warningLines(sylWarnings, successfulSearch?.expr).map((w) => <p key={w.key} className="mb-1 text-xs text-amber-700">{w.text}</p>)}
              {searchError && (
                <div className="space-y-2 p-3">
                  <p role="alert" className="text-sm text-danger">{searchError}</p>
                  {/* AND 검색이 없는 서버(본문 검색 배포 전)면 같은 조건으로 다시 눌러도 같은 벽이다 — [다시 시도] 대신
                      방식만 OR 로 바꿔 곧바로 찾는 길을 준다(오너 결정 D4). 다른 조건은 그대로 둔다. */}
                  {searchErrorOffersOr ? (
                    <Button size="xs" variant="secondary" onClick={() => { const next = { ...formRef.current, conceptMatch: 'any' }; changeForm(next); load(next, 1); }} disabled={searching}>OR로 다시 검색</Button>
                  ) : (
                    <Button size="xs" variant="secondary" onClick={() => load(form, failedSearchRef.current?.key === composeSearchKey(sylExprRef.current, formRef.current) ? failedSearchRef.current.page : 1)} disabled={!searchAllowed || searching}>다시 시도</Button>
                  )}
                </div>
              )}
              {!searchError && searching && results.items.length === 0 && (
                <div className="space-y-2 py-2">
                  {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-14 rounded-xl bg-gray-100 motion-safe:animate-pulse" />)}
                </div>
              )}
              {!searchError && !searching && !successfulSearch && <p className="p-6 text-center text-sm text-gray-400">조건을 설정하고 문제를 검색하세요.</p>}
              {!searchError && !searching && successfulSearch && results.items.length === 0 && (
                <p className="p-6 text-center text-sm text-gray-400">
                  조건에 맞는 문제가 없습니다 — 조건을 줄여 보세요.
                </p>
              )}

              <ul className="space-y-1">
                {results.items.map((it) => {
                  const id = it.problem_id;
                  const isIn = already.has(String(id));
                  const sel = picked.includes(id);
                  const origin = getOriginDisplay(it);
                  const concepts = withDict(conceptPairsOf(it));
                  const needs = NEED_BADGES.filter(([k]) => it[k]);
                  // 조판 본문이 있으면 그것을 그리고, 없으면 지금처럼 평문 요약을 그린다
                  const body = searchBodies[String(id)] ?? null;
                  // 끌어서 담을 수 있는가 — 이미 담긴 것은 끌리지 않고(놓아 봐야 계약 §1-3 에 막힌다),
                  // 번호 입력과 교체를 진행하는 동안은, 저장 중에는 목록을 건드리지 않는다
                  const canDragCard = !isIn && actionMode == null && busy == null;
                  return (
                    <li
                      key={id}
                      draggable={canDragCard}
                      onDragStart={(e) => {
                        if (!canDragCard) { e.preventDefault(); return; }
                        drag.start(e, id, 'copy');
                      }}
                      onDragEnd={endDrag}
                      onClick={(e) => {
                        // 카드 안의 진짜 조작(체크 줄 토글·미리보기·담기)은 자기 몫을 이미 했다.
                        // 여기서 또 뒤집으면 한 번 눌러 두 번 도는 꼴이 된다.
                        if (e.target.closest('button, a, input, select, textarea, label')) return;
                        // 글자를 긁어 고르고 손을 뗀 것은 「누른 것」이 아니다
                        const sels = typeof window === 'undefined' ? null : window.getSelection();
                        if (sels && !sels.isCollapsed) return;
                        if (replacing) { if (canEditNow()) setCandidateId(id); return; }
                        if (isIn) return; // 이미 담긴 것은 고를 수 없다(위 버튼도 disabled 다)
                        toggle(id);
                      }}
                      className={`flex items-start gap-1 rounded-xl border p-1.5 transition-colors ${
                        canDragCard ? 'cursor-grab active:cursor-grabbing' : ''
                      } ${
                        /* 테두리는 **늘 있다**(오너 지시 2026-08-31 「선택되지 않은 상태에서도 각
                           요소별로 영역 구분이 잘 되었으면」). 종전에는 고르지 않은 카드가
                           border-transparent 라, 본문 카드가 흰 바탕에 흰 바탕으로 이어져 어디까지가
                           한 문제인지 세로로 훑어서는 알 수 없었다. 값은 가운데 '담은 문항' 행과 같은
                           gray-200 이다 — 같은 창의 두 목록이 다른 경계선을 갖지 않게. */
                        replacing ? `cursor-pointer hover:border-primary/40 hover:bg-primary/5 ${String(candidateId) === String(id) ? 'border-primary bg-primary/5' : 'border-gray-200'}`
                          : isIn ? 'border-gray-200 bg-gray-50 opacity-70'
                            : sel ? 'border-primary/40 bg-primary/5' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {/* 잡는 곳 — **표시일 뿐이고 끌리는 것은 카드 전체다**(오너 지시 2026-08-28
                          「이 카드 요소 아무데나 선택되거나, 드래그되는 상태로 바로 되도록」).
                          손잡이를 지우지 않는 이유는 「이건 끌 수 있는 것」이라는 신호가 그림으로
                          남아야 하기 때문이다 — 커서만으로는 대 보기 전엔 알 수 없다.
                          바꾸면서 잃는 것 둘을 적어 둔다: ① 본문의 수식을 마우스로 긁어 고를 수 없다
                          (읽는 자리는 오른쪽 [눈] 버튼이 따로 연다) ② 안쪽 버튼 위에서 몇 px 흔들리면
                          브라우저가 그것을 끌기로 읽어 그 클릭이 죽는다(움직이지 않으면 그대로 눌린다).
                          끌기 아닌 대안(WCAG 2.5.7)은 [담기] 버튼이 갖는다 — 다만 그 길은 늘 맨 뒤에
                          붙고, 자리를 골라 꽂는 것은 끌기뿐이다(담은 뒤 ↑·↓ 로 옮길 수 있다). */}
                      <span
                        aria-hidden="true"
                        title={canDragCard ? '카드 아무 곳이나 잡아 담은 목록의 원하는 자리에 놓기' : undefined}
                        className={`mt-0.5 shrink-0 rounded px-0.5 py-1 text-gray-300 ${
                          canDragCard ? '' : 'opacity-40'
                        }`}
                      >
                        <GripVertical className="size-3.5" />
                      </span>
                      {/* 조판 본문은 **읽는 것**이라 선택 토글 버튼 안에 둘 수 없다 — 버튼 안에서는
                          수식을 끌어 읽을 수 없고, 조판 마크업(div·img)이 button 안에 들어가는 것도
                          HTML 위반이다. 그래서 버튼은 배지 줄까지만 맡고 본문·꼬리표는 그 밖으로 나온다.
                          평문 요약일 때는 지금처럼 버튼 안에 남긴다 — 그건 읽을 것이 아니라 꼬리표라
                          넓은 클릭 자리를 내주는 편이 낫다. */}
                      <div className="min-w-0 flex-1">
                        {/* 교체 중 후보 확인은 데이터 변경과 분리한다. 다중 담기 체크박스는 감춘다. */}
                        {/* 배지 줄과 [미리보기]·[담기]가 **한 행**이다(오너 지시 2026-08-28
                            「이 요소와 동일 행으로 … 우측끝으로」). 두 버튼이 행 오른쪽 끝에 서면
                            그 아래 폭이 통째로 본문 몫이 된다 — 본문은 지면 한 단을 축소해 그리는
                            자리라 폭이 넓을수록 배율이 올라가 글자가 커진다.
                            토글 버튼은 배지 줄까지만 맡는다. 버튼 안에 버튼을 넣을 수 없다.
                            셋의 세로 기준은 **가운데**다(오너 지시 2026-08-28) — 배지가 두 줄로
                            접히면 위 정렬에서는 [눈]·[담기]가 첫 줄에 붙어 혼자 위로 떠 보인다. */}
                        <div className="flex items-center gap-1">
                        {/* 번호는 **고르기 버튼 밖**에 선다(2026-09-01). 누르면 그 문제의 상세로
                            가는 링크인데, 버튼 안에 anchor 를 넣으면 버튼 안의 버튼이 된다.
                            자리는 체크칸 왼쪽 — 기준으로 삼은 문제 v2 의 표에서도 PID 가 첫 칸이다.
                            바깥 카드(li)의 onClick 은 `a` 위의 클릭을 이미 건너뛰므로(그 행의
                            가드 목록에 a 가 들어 있다) 번호를 눌러 카드가 함께 골라지지 않는다. */}
                        <ProblemIdLink id={id} item={it} className="shrink-0" />
                        {/* **disabled 를 쓰지 않는다**(2026-08-31). 브라우저는 disabled 인 button
                            **안쪽 전체**의 포인터 이벤트를 죽인다 — 이미 담긴 카드에서는 그 안에
                            든 개념 배지까지 함께 죽어 설명 창이 열리지 않았다(실사용 확인).
                            고르는 조작만 막으면 되므로 상태는 aria-disabled 로 말하고 조작은
                            아래 가드가 막는다. 커서 모양은 종전과 같다. */}
                        <button
                          type="button"
                          onClick={() => {
                            if (!replacing && isIn) return; // 이미 담긴 것은 고를 수 없다
                            if (!canEditNow()) return;
                            if (replacing) setCandidateId(id); else toggle(id);
                          }}
                          aria-disabled={busy != null || (!replacing && isIn)}
                          aria-pressed={replacing ? String(candidateId) === String(id) : sel}
                          /* 번호가 버튼 밖으로 나가면서 읽어 주는 이름에서 빠졌다 — 못박는다 */
                          aria-label={replacing ? `${id}번 교체 후보 확인` : `${id}번 문제 고르기`}
                          className={`min-w-0 flex-1 text-left ${!replacing && isIn ? 'cursor-not-allowed' : ''}`}
                        >
                          <div className="flex flex-wrap items-center gap-1">
                            {!replacing && (
                              <span
                                aria-hidden="true"
                                className={`flex size-4 shrink-0 items-center justify-center rounded border ${
                                  sel ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white'
                                }`}
                              >
                                {sel && <Check className="size-3" />}
                              </span>
                            )}
                            {isIn && <span className="shrink-0 rounded-full bg-gray-200 px-1.5 py-0.5 text-[9px] text-gray-600">{replacing ? `현재 ${rows.findIndex((r) => String(r.problem_id) === String(id)) + 1}번 · 순서 교체 사용` : '이미 담김'}</span>}
                            {origin && <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium ${origin.cls}`}>{origin.text}</span>}
                            {it.difficulty != null && (
                              <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] ${difficultyBadgeClass(it.difficulty)}`}>난이도 {it.difficulty}</span>
                            )}
                            {/* 이 문제의 매스컨셉(오너 지시 2026-08-29). 종전에는 카드 맨 아래
                                라벨과 한 문자열로 합쳐 10px 회색 한 줄에 있었다 — 문제를 고르는
                                단서 중 가장 중요한 것이 가장 흐린 자리에 있었던 셈이다.
                                **아래에서는 뺐다** — 같은 값을 두 곳에 그리면 카드가 시끄럽고,
                                아래 줄의 잘림 판정([모두 보기])도 라벨 몫만 재게 된다.
                                2026-08-31 로 이름만 있던 자체 배지를 걷고 **이 저장소의 정본 배지**
                                (conceptBadge.ConceptBadge — [순번][유형][이름])로 갈아탔다. 같은 개념이
                                자리에 따라 다른 얼굴이면 읽는 사람이 두 번 생각한다. 순번·유형은
                                v2 사전(계약 §8)에서 오고, 없으면 종전처럼 이름만 남는다.
                                자를 개수를 두지 않는다: 접어 두면 「무슨 개념인가」를 알려고
                                카드를 열어야 하고, 넘치면 이 줄이 flex-wrap 으로 접힌다.
                                **asSpan** — 이 줄 전체가 고르기 버튼 안이라 배지가 button 이면
                                버튼 안의 버튼이 된다. 누르면 설명 창이 열리고, 그 클릭이 카드
                                고르기까지 함께 돌지 않게 배지가 전파를 끊는다. */}
                            {concepts.map((c, i) => (
                              <ConceptBadge
                                key={`${c.id ?? 'x'}-${c.name}-${i}`}
                                name={c.name}
                                order={c.order}
                                type={c.type}
                                compact
                                asSpan
                                /* id 를 모르는 개념은 누를 수 없다 — 무엇을 물어볼지 알 수 없어서다 */
                                onClick={c.id != null ? () => openConcept(c.id) : null}
                              />
                            ))}
                            {needs.map(([k, label]) => (
                              <span key={k} className="shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] text-amber-700">{label} 없음</span>
                            ))}
                            {ANALYSIS_STATUS[it.analysis_status] && (
                              <span className="shrink-0 rounded-full bg-sky-50 px-1.5 py-0.5 text-[9px] text-sky-700">{ANALYSIS_STATUS[it.analysis_status]}</span>
                            )}
                          </div>
                        </button>
                          {/* [눈](문제 미리보기)은 걷었다(오너 지시 2026-08-31). 카드가 이미 조판
                              본문을 그대로 그리고 있어 「수식까지 그린 실제 문제」를 따로 열 일이
                              줄었다. 그 창을 띄우던 유일한 자리라 딸린 상태·창·ESC 가드도 함께 걷었다 —
                              남기면 아무도 열 수 없는 경로가 되고, 빌드는 그것을 구분하지 못한다. */}
                          <button
                            type="button"
                            onClick={() => (replacing ? putInSlot(id) : addProblems([id]))}
                            disabled={isIn || busy != null}
                            aria-label={replacing
                              ? `${id}번 문제로 ${selectedIdx + 1}번 문항 교체`
                              : `${id}번 문제 담기`}
                            /* 켜진 상태를 진하게 — 오너 지적 2026-08-28 「비활성화와 구분이 잘 안됨」.
                               꺼진 모습은 `opacity-40` 하나뿐이라, 켜진 쪽이 gray-200 테두리에
                               gray-600 글자면 둘 다 흐린 회색이라 눈이 못 가른다.
                               초록으로 올리지 않은 이유가 둘 있다 — ① 이 버튼의 초록은 이미
                               **교체 모드**(「이 자리에 넣기」)의 얼굴이라 뜻이 겹친다 ② 11px 글자를
                               #4ABC85 로 두면 흰 바탕에서 2.38:1 로 본문 하한(4.5:1) 아래다.
                               그래서 색을 바꾸는 대신 **먹색을 끝까지 올리고 굵기를 한 단** 올렸다
                               (gray-600 → gray-900 · 400 → 500 · 테두리 gray-200 → gray-400).
                               흰 채움은 행이 물들 때(고른 행 primary/5) 버튼만 떠오르게 한다. */
                            className={`shrink-0 rounded-full border px-2 py-1 text-[11px] disabled:cursor-not-allowed disabled:opacity-40 ${
                              replacing ? 'border-primary/40 text-primary hover:bg-primary/10'
                                : 'border-gray-400 bg-white font-medium text-gray-900 hover:border-primary hover:text-primary'
                            }`}
                          >
                            {replacing ? <Replace className="mr-0.5 inline size-3" /> : <Plus className="mr-0.5 inline size-3" />}
                            {replacing ? '이 문제로 교체' : '담기'}
                          </button>
                        </div>
                        {/* 라벨 — **매스컨셉 바로 아래, 본문 위**다(오너 지시 2026-08-31).
                            카드 맨 아래(본문 다음)에 있던 동안에는 문제를 고르는 단서 둘(개념·라벨)이
                            본문을 사이에 두고 갈라져, 카드 하나를 위아래로 두 번 훑어야 했다. */}
                        <LabelBadges labels={it.labels} className="mt-0.5 flex flex-wrap items-center gap-1" />
                        {body == null && (
                          <ProblemBodyPreview
                            fallback={it.preview || <span className="text-gray-300">본문 미리보기 없음</span>}
                          />
                        )}
                        {/* 조판 본문 — 지면과 같은 렌더러가 만든 HTML 이라 수식·그림이 그대로 나온다.
                            폭은 카드에 맡긴다(지면의 422px 고정을 쓰지 않는다): 좌측 열이 그보다 좁아
                            그대로 넣으면 넘치고, 축소하면 글자가 읽을 수 없게 작아진다. 줄바꿈이 지면과
                            달라지지만 이 자리는 조판 검수가 아니라 「무슨 문제인가」를 알아보는 자리다.
                            넓은 수식 한 줄은 카드를 밀지 않고 제 칸 안에서 가로로 민다.
                            마크업은 ProblemBodyPreview 한 벌로 빠졌다 — 가운데 '담은 문항'이 같은
                            부품을 compact 로 쓴다(오너 지시 「이 미리보기가 저 요소 안에도」). */}
                        {body != null && <ProblemBodyPreview html={body} />}
                        <ProblemAnswerDisclosure problemId={id} />
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* 페이지 이동 — 이 저장소의 정본 부품(DesktopPagination)의 compact 변형이다
                  (오너 지시 2026-08-31). 종전에는 [◀] 1/2992 [▶] 뿐이라 2992 페이지짜리 결과에서
                  한 번에 한 칸씩만 갈 수 있었다. 이제 번호를 눌러 뛰고, 현재 칸에 숫자를 쳐서
                  바로 갈 수도 있다. 조건·값은 형제 화면(ProblemHubV2/ProblemSearchPanel)과 같다. */}
              {results.totalPages > 1 && (
                <div className={`mt-2 ${staleSearch ? 'pointer-events-none opacity-50' : ''}`} aria-disabled={staleSearch || searching} inert={staleSearch || searching ? '' : undefined}>
                  <DesktopPagination
                    compact
                    currentPage={results.page}
                    totalPages={results.totalPages}
                    onPageChange={(p) => { if (!searchPageBlocked.current && p !== results.page) load(formRef.current, p); }}
                  />
                </div>
              )}
            </div>

            {/* 페이지를 넘겨도 선택이 유지되므로 개수를 항상 보인다 — 안 보이면 넘긴 페이지의
                선택을 잊고 [담기]를 눌러 예상보다 많이 담게 된다 */}
            <div className="flex shrink-0 items-center gap-2 border-t px-3 py-2">
              <p aria-live="polite" className="min-w-0 flex-1 truncate text-[11px] text-gray-500">
                {staleSearch && <span className="mr-1 font-medium text-amber-700">이전 검색 결과 ·</span>}
                {replacing
                  ? `새 문제의 [이 문제로 교체]를 누르면 ${selectedIdx + 1}번 문항이 바뀝니다`
                  : picked.length > 0 ? `${picked.length}개 선택됨 — 고른 순서대로 뒤에 붙습니다` : '문제를 눌러 여러 개를 고를 수 있습니다'}
              </p>
              {/* 교체 중에 뒤에 붙이는 버튼을 남겨 두면 한 자리를 바꾸려다 문항이 늘어난다 */}
              {replacing ? (
                <Button size="xs" variant="secondary" onClick={cancelEditing}>교체 취소</Button>
              ) : (
                <>
                  {picked.length > 0 && (
                    <Button size="xs" variant="secondary" onClick={() => setPicked([])}>선택 해제</Button>
                  )}
                  <Button size="xs" onClick={() => addProblems(picked)} disabled={picked.length === 0 || busy != null}>
                    {picked.length > 0 ? `${picked.length}개 담기` : '담기'}
                  </Button>
                </>
              )}
            </div>
          </section>

          {/* 담은 문제: 위쪽 행의 기존 구성과 간격은 유지하고, 동작은 카드 전체 폭을 쓴다. */}
          <section
            className={`${COL} ${paperAsColumn ? 'flex-none border-r' : ''}`}
            style={paperAsColumn ? { width: composeColumnWidth(COMPOSE_ITEMS_SHARE, sylOpen) } : undefined}
            aria-label="담은 문제"
          >
            <div className="shrink-0 border-b px-3 py-2">
              <p ref={listHeadingRef} tabIndex={-1} className="text-xs font-medium text-gray-600">문항 {rows.length}개</p>
            </div>
            <div ref={drag.listRef} {...drag.listProps}
              className={`min-h-0 flex-1 overflow-y-auto px-3 py-2 ${dropAt != null ? 'bg-primary/5 ring-2 ring-inset ring-primary' : ''}`}>
              {rows.length === 0 ? (
                <p className={`rounded-xl border border-dashed p-6 text-center text-sm ${
                  dropAt != null ? 'border-primary text-primary' : 'border-gray-200 text-gray-400'
                }`}>
                  {dropAt != null ? '여기에 놓으면 담깁니다.' : '담긴 문항이 없습니다 — [검색]에서 개념·라벨로 찾아 담으세요.'}
                </p>
              ) : (
                <ul className="space-y-1">
                  {rows.map((r, idx) => {
                    const isSelected = selectedIdx === idx;
                    const rowItem = rowItems[String(r.problem_id)];
                    const rowDifficulty = r.difficulty ?? rowItem?.difficulty ?? null;
                    const concepts = rowConcepts(r);
                    const canDrag = actionMode == null && busy == null;
                    const isDragging = dragSource?.kind === 'move' && String(dragSource.id) === String(r.problem_id);
                    const dropEdge = dropAt === idx ? 'top'
                      : dropAt === rows.length && idx === rows.length - 1 ? 'bottom' : null;
                    return (
                      <li key={r.problem_id} data-paper-row={String(r.problem_id)}
                        className={`relative min-w-0 rounded-xl border p-1.5 ${
                          isSelected ? `border-primary ${r.isDeleted ? 'bg-amber-50' : 'bg-primary/5'}`
                            : r.isDeleted ? 'border-amber-200 bg-amber-50' : 'border-gray-200'
                        } ${isDragging ? 'opacity-40' : ''}`}>
                        {dropEdge && <span aria-hidden="true"
                          className={`pointer-events-none absolute inset-x-1 h-0.5 rounded-full bg-primary ${dropEdge === 'top' ? '-top-1' : '-bottom-1'}`} />}
                        <div className="flex items-center gap-1.5">
                          <span draggable={canDrag} onDragStart={(event) => drag.start(event, r.problem_id, 'move')}
                            onDragEnd={endDrag} aria-hidden="true" title={canDrag ? '끌어서 순서 바꾸기' : undefined}
                            className={`shrink-0 rounded px-0.5 py-1 text-gray-300 ${
                              canDrag ? 'cursor-grab hover:text-gray-500 active:cursor-grabbing' : 'opacity-40'
                            }`}>
                            <GripVertical className="size-3.5" />
                          </span>
                          <div className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 rounded-lg px-1 py-0.5 text-left hover:bg-gray-100"
                            onClick={(event) => {
                              if (event.target.closest('button, a, input, select, textarea, label')) return;
                              if (window.getSelection?.()?.isCollapsed === false) return;
                              selectProblem(r.problem_id);
                            }}>
                            <div className="flex min-w-9 shrink-0 flex-col items-center">
                              <ProblemIdLink id={r.problem_id} />
                              <button type="button" data-row-select
                                onClick={() => selectProblem(r.problem_id)}
                                disabled={busy != null || dragSource != null}
                                aria-label={`${idx + 1}번 문항 선택`} aria-pressed={isSelected}
                                title="문항 선택"
                                className="w-full rounded text-center !text-[24px] font-bold leading-7 text-primary hover:bg-primary/10 focus:outline-none focus:ring-1 focus:ring-primary">
                                {String(idx + 1).padStart(2, '0')}
                              </button>
                            </div>
                            <div className="min-w-0 flex-1">
                              <OverflowRow contentKey={`${rowDifficulty ?? ''}:${concepts.length}:${concepts.map((c) => `${c.id ?? ''}${c.order ?? ''}`).join(',')}`}>
                                {r.isDeleted && <span
                                  className="shrink-0 rounded-full border border-amber-200 bg-white px-1.5 py-0.5 text-[9px] font-medium text-amber-800"
                                  title="원본 문제가 삭제됐습니다 — 이 문항이 남아 있으면 배포·복사·되돌리기가 막힙니다">
                                  삭제된 문항 — 빼주세요
                                </span>}
                                {rowDifficulty != null && <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] ${difficultyBadgeClass(rowDifficulty)}`}>난이도 {rowDifficulty}</span>}
                                {concepts.map((c, i) => <ConceptBadge key={`${c.id ?? 'x'}-${c.name}-${i}`}
                                  name={c.name} order={c.order} type={c.type} compact asSpan
                                  onClick={c.id != null ? () => openConcept(c.id) : null} />)}
                              </OverflowRow>
                              <LabelBadges labels={r.labels || rowItem?.labels} className="mt-0.5 flex flex-wrap items-center gap-1" />
                              <ProblemBodyPreview compact showToggle
                                expanded={isSelected ? selection.expanded : undefined}
                                onExpandedChange={(expanded) => setSelection((current) => (
                                  String(current?.id) === String(r.problem_id) ? { ...current, expanded } : current
                                ))}
                                html={rowBodyHtml(r.problem_id)} fallback={r.preview || r.labels} />
                            </div>
                          </div>
                          <div className="flex shrink-0 flex-col items-center">
                            <button type="button" onClick={() => move(r.problem_id, -1)}
                              disabled={busy != null || dragSource != null || idx === 0} aria-label={`${idx + 1}번 위로`}
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30">
                              <ArrowUp className="size-3.5" />
                            </button>
                            <button type="button" onClick={() => move(r.problem_id, 1)}
                              disabled={busy != null || dragSource != null || idx === rows.length - 1} aria-label={`${idx + 1}번 아래로`}
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30">
                              <ArrowDown className="size-3.5" />
                            </button>
                            <button type="button" onClick={() => removeAt(idx)} disabled={busy != null || dragSource != null}
                              aria-label={`${idx + 1}번 빼기`} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-danger disabled:opacity-30">
                              <Trash2 className="size-3.5" />
                            </button>
                          </div>
                        </div>
                        {isSelected && <PaperRowActions mode={actionMode} number={idx + 1} count={rows.length}
                          disabled={busy != null || dragSource != null} onMode={startAction} onCancel={finishAction} onCommit={commitNumber} />}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>

          {/* ── 우: 지면 미리보기(실제 지면의 축소판) ───────────────────
              강의 칸을 뺀 열 상자 폭이 1222 미만이면 **서랍**이다(paperAsColumn 거짓 — 강의 칸을 접으면 화면 1280, 펴면 약 1607 이 경계). 겉껍질이 미끄러지는 일을, 알맹이가 보이고 안 보이는
              일을 맡는다 — 둘을 한 요소에 얹으면 손잡이까지 함께 사라진다.
              닫는 방법은 transform 이다(display:none 이 아니다). 그래야 ① 미끄러지는 것이
              보이고 ② 지면이 계속 제 폭으로 그려져 있어 열자마자 배율이 맞는다. */}
          <div
            /* min-w-0 을 빼면 세 번째 열로 설 때(xl) **지면이 앞의 두 열을 밀어낸다.**
               가로로 놓인 flex 항목의 기본 min-width 는 auto 라, 안에 든 지면이 요구하는 폭
               아래로 줄어들지 않는다 — 배율을 100% 로 올리면 이 칸이 1264px 을 달라고 한다.
               실측(하네스, 화면 1440): 없을 때 지면 칸 1264 / 넣은 뒤 528. */
            className={`flex min-h-0 min-w-0 ${paperAsColumn
              ? 'static flex-1'
              : `absolute inset-y-0 right-0 z-20 transition-transform duration-300 ease-out ${paperOpen ? 'translate-x-0' : 'translate-x-full'}`
            }`}
            /* 서랍 폭 = 지면 실치수(배율 100%)가 들어가는 폭, 넓어도 상자의 86% 까지(오너 2026-09-14 「접을 때 폭을 너무 크게」).
               86% 만 두면 1440 에서 1176px 이라 A4 가 143% 로 부풀었다. 좁은 화면에서는 86% 가 먼저 닿는다 */
            style={paperAsColumn ? undefined : { width: `min(86%, ${sheet.drawerPx}px)` }}
          >
            {/* 손잡이 — 공용 부품(PaperSheetHandle). 지면이 열로 서 있으면 감춘다(위 pr-9 이 비워 둔 자리에 앉는다) */}
            <PaperSheetHandle
              open={paperOpen}
              onToggle={() => setPaperOpen((v) => !v)}
              controls="compose-paper-panel"
              hidden={paperAsColumn}
              openTitle="담은 문제가 앉은 실제 지면을 폅니다"
            />

            {/* 알맹이 — 닫혀 있는 동안 invisible 이라 Tab 이 서랍 안으로 들어가지 않는다.
                visibility 를 전환 목록에 올려 두면 **사라지는 것만 끝에서 일어난다**(CSS 규칙).
                덕분에 열 때는 즉시 보이고, 닫을 때는 다 미끄러진 뒤에 꺼진다.
                세 번째 열로 설 때(paperAsColumn)는 늘 visible 이다. */}
            <section
              id="compose-paper-panel"
              className={`flex min-h-0 min-w-0 flex-1 flex-col bg-white transition-[visibility] duration-300 ${
                paperAsColumn ? 'visible' : `shadow-2xl ${paperOpen ? 'visible' : 'invisible'}`
              }`}
              aria-label="시험지 미리보기"
            >
              {/* 머리·스크롤 칸·지면 — 공용 부품(PaperSheetView). 문항 클릭은 이 창의 교체로 이어진다 */}
              <PaperSheetView
                sheet={sheet}
                hint={rows.length === 0
                  ? '담은 문제가 여기에 실제 지면으로 놓입니다.'
                  : '문항을 선택한 뒤 카드에서 교체하거나 순서를 바꿀 수 있습니다.'}
                emptyText="담긴 문항이 없습니다 — [검색]에서 찾아 담으면 실제 지면이 여기 그려집니다."
                onPaperClick={onPaperClick}
              />
            </section>
          </div>
        </div>
      </Modal>

      {/* 닫기 확인 — 이 모달(9999970)보다 위(기본 9999980)에 떠야 뒤 화면을 덮는다.
          확인을 받아도 dirty 는 내리지 않는다: [계속 편집]으로 돌아온 사람에게 경고가 남아야 한다. */}
      <ConfirmModal
        isOpen={confirmClose}
        onClose={() => setConfirmClose(false)}
        onConfirm={() => onClose?.()}
        title="저장하지 않은 변경이 있습니다"
        message={closeConfirmMessage}
        confirmText={closeConfirmText}
        cancelText="계속 편집"
        confirmVariant="danger"
      />

      <HubAssignmentConfirm
        isOpen={confirmAssign != null}
        onClose={() => setConfirmAssign(null)}
        onConfirm={() => submit(true)}
        studentName={assignmentTargetName}
        paperTitle={confirmAssign?.title || ''}
        problemCount={confirmAssign?.count || 0}
        busy={busy === 'assign'}
      />

      <PaperSheetFullPreview sheet={sheet} />

      {/* 개념 설명 — 배지를 누르면 뜬다. 위 Modal 의 **밖**에 두는 이유는 문제 미리보기와 같고
          (Tab 가두기), 겹침도 같은 셈이다: 공용 Modal 기본값(9999980) > 조립 창(9999970). */}
      {conceptDetail && (
        <MathceptDetailModal
          isOpen
          onClose={() => setConceptDetail(null)}
          /* data 를 주면 모달이 자체 조회(v1)를 건너뛴다 — 이 화면이 v2 전용인 근거다 */
          data={conceptDetail.data}
          loading={conceptDetail.loading}
          handleAuth={false}
        />
      )}
    </ProblemNavGuard>
  );
}

export default PaperComposeModal;
